// ============================================================================
// CHANGO V5.0 — Quote chain workspace
// ============================================================================
// Layout:
//   - Left rail: customer context + quote path
//   - Center: quote head + A/B/C/E module workbench
//   - Right rail: fixed decision cockpit
//
// Role gate: boss | finance only. Sales users are redirected to /403 because
// the wizard requires entering unit_cost_rmb (§20 #17 forbids exposing cost
// to sales). They can view read-only quote details instead.
//
// Contract with backend:
//   POST   /api/quotes               → create draft (id returned)
//   PATCH  /api/quotes/:id           → update draft (wholesale replace)
//   POST   /api/quotes/:id/submit    → draft → pending
//
// Live totals come from `previewTotals()` client-side; the authoritative
// numbers are the ones the backend writes back on save (we overwrite local
// state from the response).
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import { hasWorkspaceLayer } from '../../utils/layerPermissions.js';
import { trackEvent } from '../../lib/telemetry.js';
import { useUndoRedo, useUndoRedoShortcuts } from '../../hooks/useUndoRedo.js';
import {
  SUPPORTED_CURRENCIES,
  CURRENCY_LABEL,
  previewTotals,
  formatRmb,
  formatFx
} from '../../lib/marginPreview.js';
import ProductLookup from '../../components/ProductLookup.jsx';
import { computeAutofillPatch } from '../../lib/productAutofill.js';

// ---------------------------------------------------------------------------
// Form defaults / helpers
// ---------------------------------------------------------------------------

function emptyLine() {
  return {
    sku: '', description: '', qty: 1,
    hs_code: '', tax_rebate_rate: null, tax_rebate_source: null,
    unit_sell_rmb: 0, unit_cost_rmb: 0, line_extra_cost_rmb: 0
  };
}

function emptyShared() {
  return { label: '', amount_rmb: 0 };
}

const DEFAULT_LAYER_C_DATA = Object.freeze({
  cost_basis_mode: 'inherit_trade',
  manual_basis_cost_per_unit_rmb: 0,
  channel_cost_basis_mode: 'carry_trade',
  manual_channel_base_cost_rmb: 0,
  channel_action_mode: 'carry',
  channel_input_source: 'trade_landed_cost',
  platform_commission_pct: 0,
  payment_fee_pct: 0,
  ad_rate_pct: 0,
  return_rate_pct: 0,
  fba_fee_per_unit_rmb: 0,
  overseas_storage_rmb: 0,
  after_sales_cost_rmb: 0,
  channel_tax_rmb: 0,
  settlement_fee_rmb: 0,
  inventory_capital_cost_rmb: 0,
  other_channel_cost_rmb: 0
});

const DEFAULT_LAYER_B_DATA = Object.freeze({
  trade_cost_basis_mode: 'carry_factory',
  trade_cost_source: 'factory_output',
  trade_input_source: 'factory_output',
  trade_action_mode: 'carry',
  trade_quote_mode: 'line_sell',
  manual_trade_quote_rmb: 0,
  manual_trade_base_cost_rmb: 0,
  hs_rebate_source_enabled: false,
  logistics_source_enabled: false,
  trade_fee_source_locked: false,
  sea_freight_rmb: 0,
  insurance_rmb: 0,
  customs_declaration_rmb: 0,
  port_misc_rmb: 0,
  customs_clearance_rmb: 0,
  duty_rmb: 0,
  inland_transport_rmb: 0,
  inspection_rmb: 0,
  payment_collection_fee_rmb: 0,
  financing_cost_rmb: 0,
  sales_commission_rmb: 0,
  risk_reserve_rmb: 0,
  other_trade_cost_rmb: 0,
  hs_rebate_items: []
});

const MANUFACTURING_FEE_MODE_OPTIONS = Object.freeze([
  ['total_allocation', '总数分摊'],
  ['per_unit', '单件录入'],
  ['percentage', '比例分摊'],
  ['manual_override', '手动覆盖']
]);

const MANUFACTURING_FEE_MODE_ALIASES = Object.freeze({
  unit_input: 'per_unit',
  ratio_allocation: 'percentage'
});

function normalizeManufacturingFeeMode(value, fallback = 'per_unit') {
  const normalized = MANUFACTURING_FEE_MODE_ALIASES[value] || value;
  return MANUFACTURING_FEE_MODE_OPTIONS.some(([key]) => key === normalized)
    ? normalized
    : fallback;
}

const MANUFACTURING_FEE_BASIS_OPTIONS = Object.freeze([
  ['quote_qty', '按报价数量'],
  ['standard_hours', '按标准工时'],
  ['material_cost', '按物料金额'],
  ['process_cost', '按工序金额'],
  ['manual', '手动口径']
]);

const TRADE_COST_RULE_OPTIONS = Object.freeze([
  ['fixed_order', '整单固定'],
  ['sales_pct', '按销售额比例'],
  ['cost_pct', '按成本比例'],
  ['destination_rule', '按目的港规则'],
  ['manual', '手动录入']
]);

const REBATE_TREATMENT_OPTIONS = Object.freeze([
  ['none', '不退税', '退税不进入本单利润。'],
  ['factory_node', '工厂节点退税', '退税归属工厂节点。'],
  ['trade_node', '外贸节点退税', '退税归属外贸节点。'],
  ['entity_node', '运营主体节点退税', '退税归属运营主体节点。']
]);

const REBATE_OWNER_BY_TREATMENT = Object.freeze({
  none: 'none',
  factory_node: 'factory',
  trade_node: 'trade',
  entity_node: 'entity'
});

function normalizeRebateTreatment(value, owner) {
  if (value === 'none' || value === 'factory_node' || value === 'trade_node' || value === 'entity_node') return value;
  if (owner === 'none') return 'none';
  if (owner === 'trade_company' || owner === 'trade') return 'trade_node';
  if (owner === 'overseas_entity' || owner === 'entity') return 'entity_node';
  return 'factory_node';
}

const ENTITY_EXPENSE_METHOD_OPTIONS = Object.freeze([
  ['sales_share', '按本单销售额占比'],
  ['order_qty', '按订单数量'],
  ['entity_usage', '按主体使用比例'],
  ['auto_tax', '按当地规则'],
  ['manual', '手动分摊']
]);

const ENTITY_EXPENSE_METHOD_ALIASES = Object.freeze({
  sales_pct: 'sales_share',
  ratio: 'sales_share'
});

function normalizeEntityExpenseMethod(value, fallback = 'sales_share') {
  const normalized = ENTITY_EXPENSE_METHOD_ALIASES[value] || value;
  return ENTITY_EXPENSE_METHOD_OPTIONS.some(([key]) => key === normalized)
    ? normalized
    : fallback;
}

function defaultEntityExpenseItems() {
  return [
    { label: '房租水电网', entity_name: 'E1 海外运营主体', total_amount_rmb: 0, allocation_method: 'sales_share', allocation_pct: 0, allocated_amount_rmb: 0 },
    { label: '办公费', entity_name: 'E1 海外运营主体', total_amount_rmb: 0, allocation_method: 'sales_share', allocation_pct: 0, allocated_amount_rmb: 0 },
    { label: '工资奖金', entity_name: 'E1 海外运营主体', total_amount_rmb: 0, allocation_method: 'entity_usage', allocation_pct: 0, allocated_amount_rmb: 0 },
    { label: '税收/社保及地方费', entity_name: 'E1 海外运营主体', total_amount_rmb: 0, allocation_method: 'auto_tax', allocation_pct: 0, allocated_amount_rmb: 0 },
    { label: '其他支出', entity_name: 'E1 海外运营主体', total_amount_rmb: 0, allocation_method: 'manual', allocation_pct: 0, allocated_amount_rmb: 0 }
  ];
}

const DEFAULT_LAYER_A_DATA = Object.freeze({
  quote_logic: 'forward',
  business_chain: 'factory_trade_channel_entity',
  factory_action_mode: 'forward',
  factory_input_mode: 'bom',
  target_market: '',
  factory_business_type: 'direct_export',
  factory_sales_target: 'foreign_customer',
  customs_subject: 'factory',
  collection_subject: 'factory',
  rebate_treatment: 'factory_node',
  rebate_owner: 'factory',
  output_term: 'EXW',
  factory_handles_rebate: true,
  manufacturing_fee_mode: 'total_allocation',
  manufacturing_fee_allocation_basis: 'quote_qty',
  small_batch_uplift_pct: 0,
  factory_margin_pct: 18,
  capital_days: 45,
  material_items: [
    {
      name: '主材',
      spec_source: '',
      consumption: 1,
      unit: '件',
      tax_included_unit_price_rmb: 0,
      input_vat_rate_pct: 13
    }
  ],
  process_items: [
    {
      name: '工序',
      standard_hours: 0,
      labor_rate_rmb_per_hour: 0,
      equipment_energy_rmb: 0,
      input_vat_rate_pct: 0
    }
  ],
  manufacturing_fees: [
    {
      label: '制造费用',
      allocation_rule: '按件',
      total_amount_rmb: 0,
      unit_amount_rmb: 0,
      ratio_pct: 0,
      amount_rmb: 0,
      input_vat_rate_pct: 0
    }
  ]
});

const DEFAULT_LAYER_E_DATA = Object.freeze({
  entity_action_mode: 'carry',
  entity_input_source: 'trade_profit',
  overseas_profit_share_pct: 30,
  transfer_pricing_pct: 30,
  china_income_tax_pct: 25,
  domestic_operating_expense_rmb: 0,
  settlement_path: '平台 → 海外主体 → 国内分红/服务费',
  entity_expense_items: defaultEntityExpenseItems(),
  overseas_entities: [
    {
      name: '海外运营主体 1',
      profit_share_pct: 100,
      operating_expense_rmb: 0,
      income_tax_pct: 0,
      flow_tax_pct: 0
    }
  ]
});

const QUOTE_LOGIC_OPTIONS = Object.freeze([
  ['forward', '正算'],
  ['reverse', '倒推'],
  ['mixed', '混合']
]);

const BUSINESS_CHAIN_OPTIONS = Object.freeze([
  ['factory_trade', '工厂 → 外贸（自产 EXW/FOB）'],
  ['supplier_trade', '外部工厂 → 外贸'],
  ['trade_entity', '外贸 → 运营主体层'],
  ['trade_buyer', '外贸 → 外部买方'],
  ['channel_reverse', '电商售价 → B/DDP → 反推采购价'],
  ['factory_trade_channel_entity', '工厂 → 外贸 → 电商 → 运营主体层']
]);

const QUOTE_PATH_OPTIONS = Object.freeze([
  {
    key: 'factory_trade',
    title: '自有工厂生产报价',
    desc: '从自产 BOM、工序和制造费用生成 EXW/FOB，并可承接到外贸报价。',
    chain: 'factory_trade'
  },
  {
    key: 'supplier_trade',
    title: '外采供应商报价',
    desc: '外部供应商含税报价进入，作为 B 外贸报价的基础采购成本。',
    chain: 'supplier_trade'
  },
  {
    key: 'trade_entity',
    title: '出口到运营主体层',
    desc: '外贸出口后进入运营主体层，合并国内、海外和集团利润。',
    chain: 'trade_entity'
  },
  {
    key: 'trade_buyer',
    title: '出口到外部买方',
    desc: '外部买方作为客户，不合并买方海外利润。',
    chain: 'trade_buyer'
  },
  {
    key: 'channel_reverse',
    title: '电商售价反推',
    desc: '从终端售价、渠道扣点和目标利润反推最大 DDP 或采购价。',
    chain: 'channel_reverse'
  },
  {
    key: 'factory_trade_channel_entity',
    title: '全链路报价',
    desc: '工厂、外贸、电商和运营主体层全部纳入一张报价链。',
    chain: 'factory_trade_channel_entity'
  }
]);

function cloneLayerAData(raw) {
  const source = raw?.inputs && typeof raw.inputs === 'object' ? raw.inputs : (raw || DEFAULT_LAYER_A_DATA);
  const materialSource = Array.isArray(source.material_items) && source.material_items.length > 0
    ? source.material_items
    : (Array.isArray(source.materials) && source.materials.length > 0 ? source.materials : DEFAULT_LAYER_A_DATA.material_items);
  const processSource = Array.isArray(source.process_items) && source.process_items.length > 0
    ? source.process_items
    : (Array.isArray(source.processes) && source.processes.length > 0 ? source.processes : DEFAULT_LAYER_A_DATA.process_items);
  const inferredFeeMode = source.manufacturing_fee_mode
    || (Array.isArray(source.manufacturing_fees) && source.manufacturing_fees.some((row) => row?.total_amount_rmb || row?.ratio_pct)
      ? DEFAULT_LAYER_A_DATA.manufacturing_fee_mode
      : 'per_unit');
  return {
    ...DEFAULT_LAYER_A_DATA,
    ...source,
    rebate_treatment: normalizeRebateTreatment(source.rebate_treatment, source.rebate_owner),
    rebate_owner: REBATE_OWNER_BY_TREATMENT[
      normalizeRebateTreatment(source.rebate_treatment, source.rebate_owner)
    ],
    manufacturing_fee_mode: normalizeManufacturingFeeMode(inferredFeeMode, DEFAULT_LAYER_A_DATA.manufacturing_fee_mode),
    manufacturing_fee_allocation_basis: source.manufacturing_fee_allocation_basis || DEFAULT_LAYER_A_DATA.manufacturing_fee_allocation_basis,
    material_items: materialSource.map((row, idx) => ({
      name: row?.name || (idx === 0 ? '主材' : `物料 ${idx + 1}`),
      spec_source: row?.spec_source || row?.source || '',
      consumption: toNumber(row?.consumption ?? row?.qty ?? row?.quantity ?? 0),
      unit: row?.unit || '件',
      tax_included_unit_price_rmb: toNumber(row?.tax_included_unit_price_rmb ?? row?.tax_inclusive_unit_price_rmb ?? row?.unit_price_rmb ?? 0),
      input_vat_rate_pct: toNumber(row?.input_vat_rate_pct ?? 13)
    })),
    process_items: processSource.map((row, idx) => ({
      name: row?.name || (idx === 0 ? '工序' : `工序 ${idx + 1}`),
      standard_hours: toNumber(row?.standard_hours ?? row?.hours ?? row?.work_hours ?? 0),
      labor_rate_rmb_per_hour: toNumber(row?.labor_rate_rmb_per_hour ?? row?.labor_rate_rmb ?? row?.hourly_rate_rmb ?? 0),
      equipment_energy_rmb: toNumber(row?.equipment_energy_rmb ?? 0),
      input_vat_rate_pct: toNumber(row?.input_vat_rate_pct ?? 0)
    })),
    manufacturing_fees: (Array.isArray(source.manufacturing_fees) && source.manufacturing_fees.length > 0
      ? source.manufacturing_fees
      : DEFAULT_LAYER_A_DATA.manufacturing_fees
    ).map((row, idx) => ({
      label: row?.label || (idx === 0 ? '制造费用' : `费用 ${idx + 1}`),
      allocation_rule: row?.allocation_rule || '按件',
      total_amount_rmb: toNumber(row?.total_amount_rmb ?? row?.fee_total_rmb ?? 0),
      unit_amount_rmb: toNumber(row?.unit_amount_rmb ?? row?.amount_per_unit_rmb ?? row?.amount_rmb ?? 0),
      ratio_pct: toNumber(row?.ratio_pct ?? row?.allocation_ratio_pct ?? 0),
      amount_rmb: toNumber(row?.amount_rmb ?? 0),
      manufacturing_fee_mode: normalizeManufacturingFeeMode(row?.manufacturing_fee_mode ?? row?.mode, null),
      input_vat_rate_pct: toNumber(row?.input_vat_rate_pct ?? 0)
    }))
  };
}

function clampSignedRate(value) {
  const n = toNumber(value);
  return Math.min(1, Math.max(-1, n));
}

function emptyHsRebateItem(seed = {}) {
  return {
    hs_code: seed.hs_code || '',
    label: seed.label || seed.name || '',
    base_amount_rmb: toNumber(seed.base_amount_rmb ?? seed.amount_rmb ?? 0),
    tax_rebate_rate: clampSignedRate(seed.tax_rebate_rate ?? seed.rebate_rate ?? 0)
  };
}

function distributeUnitCostByQty(lineItems = [], totalCostRmb = 0) {
  const lines = Array.isArray(lineItems) && lineItems.length > 0 ? lineItems : [emptyLine()];
  const totalQty = totalQuoteQty(lines);
  const unitCost = totalQty > 0 ? toNumber(totalCostRmb) / totalQty : 0;
  return lines.map((line) => ({ ...line, unit_cost_rmb: unitCost }));
}

function distributeUnitSellByQty(lineItems = [], totalSellRmb = 0) {
  const lines = Array.isArray(lineItems) && lineItems.length > 0 ? lineItems : [emptyLine()];
  const totalQty = totalQuoteQty(lines);
  const unitSell = totalQty > 0 ? toNumber(totalSellRmb) / totalQty : 0;
  return lines.map((line) => ({ ...line, unit_sell_rmb: unitSell }));
}

function deriveLineHsRebateItems(lineItems = []) {
  return (Array.isArray(lineItems) ? lineItems : [])
    .map((line, idx) => {
      const hsCode = String(line?.hs_code || '').trim();
      const rate = line?.tax_rebate_rate == null || line?.tax_rebate_rate === ''
        ? null
        : clampSignedRate(line.tax_rebate_rate);
      if (!hsCode && rate == null) return null;
      return emptyHsRebateItem({
        hs_code: hsCode,
        label: line?.description || line?.sku || `商品行 ${idx + 1}`,
        base_amount_rmb: toNumber(line?.unit_sell_rmb) * Math.max(1, Math.round(toNumber(line?.qty))),
        tax_rebate_rate: rate ?? 0
      });
    })
    .filter(Boolean);
}

function cloneLayerBData(raw) {
  const source = raw?.inputs && typeof raw.inputs === 'object' ? raw.inputs : (raw || DEFAULT_LAYER_B_DATA);
  const rows = Array.isArray(source.hs_rebate_items) ? source.hs_rebate_items : [];
  const tradeCostSource = source.trade_cost_source
    || (source.trade_input_source === 'external_supplier' ? 'external_supplier' : null)
    || (source.trade_cost_basis_mode === 'manual' ? 'manual_purchase' : 'factory_output');
  return {
    ...DEFAULT_LAYER_B_DATA,
    ...source,
    trade_cost_source: tradeCostSource,
    trade_input_source: source.trade_input_source || tradeCostSource,
    trade_quote_mode: source.trade_quote_mode || DEFAULT_LAYER_B_DATA.trade_quote_mode,
    manual_trade_quote_rmb: toNumber(source.manual_trade_quote_rmb),
    manual_trade_base_cost_rmb: toNumber(source.manual_trade_base_cost_rmb),
    hs_rebate_source_enabled: Boolean(source.hs_rebate_source_enabled || source.trade_input_source === 'hs_rebate'),
    logistics_source_enabled: Boolean(source.logistics_source_enabled || source.trade_input_source === 'logistics'),
    trade_fee_source_locked: Boolean(source.trade_fee_source_locked),
    sea_freight_rmb: toNumber(source.sea_freight_rmb),
    insurance_rmb: toNumber(source.insurance_rmb),
    customs_declaration_rmb: toNumber(source.customs_declaration_rmb),
    port_misc_rmb: toNumber(source.port_misc_rmb),
    customs_clearance_rmb: toNumber(source.customs_clearance_rmb),
    duty_rmb: toNumber(source.duty_rmb),
    inland_transport_rmb: toNumber(source.inland_transport_rmb),
    inspection_rmb: toNumber(source.inspection_rmb),
    payment_collection_fee_rmb: toNumber(source.payment_collection_fee_rmb),
    financing_cost_rmb: toNumber(source.financing_cost_rmb),
    sales_commission_rmb: toNumber(source.sales_commission_rmb),
    risk_reserve_rmb: toNumber(source.risk_reserve_rmb),
    other_trade_cost_rmb: toNumber(source.other_trade_cost_rmb),
    hs_rebate_items: rows.map((row) => emptyHsRebateItem(row))
  };
}

function defaultForm() {
  return {
    customer_id: null,
    currency: 'CNY',
    fx_rate_to_rmb: 1,
    fx_rate_confirmed: true,
    business_chain: 'factory_trade_channel_entity',
    lang: 'zh',
    discount_pct: 0,
    tax_rate_pct: 0,
    hs_code: '',
    tax_rebate_rate: null,
    tax_rebate_source: null,
    line_items: [emptyLine()],
    shared_costs: [],
    layer_a_data: cloneLayerAData(),
    layer_b_data: cloneLayerBData(),
    layer_c_data: { ...DEFAULT_LAYER_C_DATA },
    layer_e_data: layerInputs(null, DEFAULT_LAYER_E_DATA)
  };
}

function toNumber(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function roundPercent(v) {
  if (!Number.isFinite(v)) return 0;
  return Math.round(v * 100) / 100;
}

function totalQuoteQty(lineItems = []) {
  return lineItems.reduce((acc, item) => {
    const n = Number.parseInt(item?.qty, 10);
    return acc + (Number.isInteger(n) && n > 0 ? n : 0);
  }, 0);
}

function csvCell(value) {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function buildFactoryTemplateCsv(layerA) {
  const headers = [
    '模块', '名称', '规格/来源', '单耗', '单位', '含税单价', '进项税率',
    '标准工时', '人工费率', '设备/能耗', '分摊规则', '费用总额', '金额每件', '比例'
  ];
  const rows = [
    ...layerA.material_items.map((row) => [
      '物料', row.name, row.spec_source, row.consumption, row.unit,
      row.tax_included_unit_price_rmb, row.input_vat_rate_pct, '', '', '', '', '', '', ''
    ]),
    ...layerA.process_items.map((row) => [
      '工序', row.name, '', '', '', '', row.input_vat_rate_pct,
      row.standard_hours, row.labor_rate_rmb_per_hour, row.equipment_energy_rmb,
      '', '', '', ''
    ]),
    ...layerA.manufacturing_fees.map((row) => [
      '制造费用', row.label, '', '', '', '', row.input_vat_rate_pct,
      '', '', '', row.allocation_rule, row.total_amount_rmb, row.unit_amount_rmb ?? row.amount_rmb, row.ratio_pct
    ])
  ];
  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
}

function splitDelimitedLine(line, delimiter) {
  const cells = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (ch === delimiter && !quoted) {
      cells.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
}

function parseFactoryTemplateText(text) {
  const lines = String(text || '').replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return null;
  const delimiter = lines[0].includes('\t') ? '\t' : ',';
  const headers = splitDelimitedLine(lines[0], delimiter);
  const material_items = [];
  const process_items = [];
  const manufacturing_fees = [];
  let importedFeeMode = null;

  for (const line of lines.slice(1)) {
    const cells = splitDelimitedLine(line, delimiter);
    const row = {};
    headers.forEach((header, idx) => { row[header] = cells[idx] ?? ''; });
    const section = row['模块'];
    if (section === '物料') {
      material_items.push({
        name: row['名称'] || `物料 ${material_items.length + 1}`,
        spec_source: row['规格/来源'] || '',
        consumption: toNumber(row['单耗']),
        unit: row['单位'] || '件',
        tax_included_unit_price_rmb: toNumber(row['含税单价']),
        input_vat_rate_pct: toNumber(row['进项税率'] || 13)
      });
    } else if (section === '工序') {
      process_items.push({
        name: row['名称'] || `工序 ${process_items.length + 1}`,
        standard_hours: toNumber(row['标准工时']),
        labor_rate_rmb_per_hour: toNumber(row['人工费率']),
        equipment_energy_rmb: toNumber(row['设备/能耗']),
        input_vat_rate_pct: toNumber(row['进项税率'])
      });
    } else if (section === '制造费用') {
      const unitAmount = toNumber(row['金额每件'] || row['金额/件']);
      const totalAmount = toNumber(row['费用总额']);
      const ratioPct = toNumber(row['比例'] || row['比例%']);
      if (!importedFeeMode && totalAmount > 0) importedFeeMode = 'total_allocation';
      if (!importedFeeMode && ratioPct > 0) importedFeeMode = 'percentage';
      manufacturing_fees.push({
        label: row['名称'] || `费用 ${manufacturing_fees.length + 1}`,
        allocation_rule: row['分摊规则'] || '按件',
        total_amount_rmb: totalAmount,
        unit_amount_rmb: unitAmount,
        ratio_pct: ratioPct,
        amount_rmb: unitAmount,
        input_vat_rate_pct: toNumber(row['进项税率'])
      });
    }
  }

  return { material_items, process_items, manufacturing_fees, manufacturing_fee_mode: importedFeeMode || 'per_unit' };
}

function textFromXmlNode(node) {
  return Array.from(node?.getElementsByTagName('t') || [])
    .map((item) => item.textContent || '')
    .join('');
}

function columnIndexFromCellRef(ref) {
  const letters = String(ref || '').match(/^[A-Z]+/i)?.[0] || '';
  return letters.toUpperCase().split('').reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0) - 1;
}

async function inflateZipEntry(bytes) {
  if (typeof DecompressionStream !== 'function') {
    throw new Error('当前浏览器不支持直接解析 Excel，请导入 CSV。');
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

function findZipEndOfCentralDirectory(view) {
  const min = Math.max(0, view.byteLength - 0x10000 - 22);
  for (let offset = view.byteLength - 22; offset >= min; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) return offset;
  }
  return -1;
}

async function unzipEntries(buffer) {
  const view = new DataView(buffer);
  const eocdOffset = findZipEndOfCentralDirectory(view);
  if (eocdOffset < 0) throw new Error('未识别到有效的 Excel 文件。');

  const entryCount = view.getUint16(eocdOffset + 10, true);
  let cursor = view.getUint32(eocdOffset + 16, true);
  const decoder = new TextDecoder('utf-8');
  const entries = new Map();

  for (let i = 0; i < entryCount; i += 1) {
    if (view.getUint32(cursor, true) !== 0x02014b50) break;
    const method = view.getUint16(cursor + 10, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const fileNameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localHeaderOffset = view.getUint32(cursor + 42, true);
    const nameBytes = new Uint8Array(buffer, cursor + 46, fileNameLength);
    const name = decoder.decode(nameBytes);
    cursor += 46 + fileNameLength + extraLength + commentLength;

    if (view.getUint32(localHeaderOffset, true) !== 0x04034b50) continue;
    const localNameLength = view.getUint16(localHeaderOffset + 26, true);
    const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
    const dataOffset = localHeaderOffset + 30 + localNameLength + localExtraLength;
    const compressed = new Uint8Array(buffer, dataOffset, compressedSize);
    let bytes;
    if (method === 0) {
      bytes = compressed;
    } else if (method === 8) {
      bytes = await inflateZipEntry(compressed);
    } else {
      continue;
    }
    entries.set(name, decoder.decode(bytes));
  }

  return entries;
}

function firstWorksheetPath(entries, parser) {
  const workbookXml = entries.get('xl/workbook.xml');
  const relsXml = entries.get('xl/_rels/workbook.xml.rels');
  if (!workbookXml || !relsXml) return 'xl/worksheets/sheet1.xml';
  const workbook = parser.parseFromString(workbookXml, 'application/xml');
  const rels = parser.parseFromString(relsXml, 'application/xml');
  const firstSheet = workbook.getElementsByTagName('sheet')[0];
  const relId = firstSheet?.getAttribute('r:id') || firstSheet?.getAttribute('id');
  if (!relId) return 'xl/worksheets/sheet1.xml';
  const rel = Array.from(rels.getElementsByTagName('Relationship'))
    .find((item) => item.getAttribute('Id') === relId);
  const target = rel?.getAttribute('Target');
  if (!target) return 'xl/worksheets/sheet1.xml';
  return target.startsWith('xl/') ? target : `xl/${target.replace(/^\//, '')}`;
}

async function parseXlsxToFactoryText(file) {
  const entries = await unzipEntries(await file.arrayBuffer());
  const parser = new DOMParser();
  const sharedStringsXml = entries.get('xl/sharedStrings.xml');
  const sharedStrings = sharedStringsXml
    ? Array.from(parser.parseFromString(sharedStringsXml, 'application/xml').getElementsByTagName('si')).map(textFromXmlNode)
    : [];
  const sheetPath = firstWorksheetPath(entries, parser);
  const sheetXml = entries.get(sheetPath) || entries.get('xl/worksheets/sheet1.xml');
  if (!sheetXml) throw new Error('Excel 中没有可读取的工作表。');

  const sheet = parser.parseFromString(sheetXml, 'application/xml');
  const rows = Array.from(sheet.getElementsByTagName('row')).map((rowNode) => {
    const cells = [];
    Array.from(rowNode.getElementsByTagName('c')).forEach((cellNode) => {
      const idx = columnIndexFromCellRef(cellNode.getAttribute('r'));
      const type = cellNode.getAttribute('t');
      const rawValue = cellNode.getElementsByTagName('v')[0]?.textContent || '';
      const inlineText = textFromXmlNode(cellNode);
      let value = rawValue;
      if (type === 's') value = sharedStrings[Number(rawValue)] || '';
      if (type === 'inlineStr') value = inlineText;
      cells[Math.max(0, idx)] = value;
    });
    return cells;
  });

  return rows.map((row) => row.map(csvCell).join(',')).join('\n');
}

async function readFactoryTemplateFile(file) {
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.xlsx')) return parseXlsxToFactoryText(file);
  return file.text();
}

function layerInputs(data, defaults) {
  const raw = !data || typeof data !== 'object'
    ? {}
    : (data.inputs && typeof data.inputs === 'object' ? data.inputs : data);
  const merged = { ...defaults, ...raw };
  if (Array.isArray(defaults.entity_expense_items)) {
    const items = Array.isArray(raw.entity_expense_items) && raw.entity_expense_items.length > 0
      ? raw.entity_expense_items
      : defaults.entity_expense_items;
    merged.entity_expense_items = items.map((item, idx) => ({
      label: item?.label || defaultEntityExpenseItems()[idx]?.label || `主体费用 ${idx + 1}`,
      entity_name: item?.entity_name || item?.entity || 'E1 海外运营主体',
      total_amount_rmb: toNumber(item?.total_amount_rmb),
      allocation_method: normalizeEntityExpenseMethod(item?.allocation_method, defaultEntityExpenseItems()[idx]?.allocation_method || 'sales_share'),
      allocation_pct: toNumber(item?.allocation_pct),
      allocated_amount_rmb: toNumber(item?.allocated_amount_rmb)
    }));
  }
  if (Array.isArray(defaults.overseas_entities)) {
    const entities = Array.isArray(raw.overseas_entities) && raw.overseas_entities.length > 0
      ? raw.overseas_entities
      : defaults.overseas_entities;
    merged.overseas_entities = entities.map((entity, idx) => ({
      name: entity?.name || `海外运营主体 ${idx + 1}`,
      profit_share_pct: toNumber(entity?.profit_share_pct ?? (entities.length === 1 ? 100 : 0)),
      operating_expense_rmb: toNumber(entity?.operating_expense_rmb),
      income_tax_pct: toNumber(entity?.income_tax_pct ?? entity?.tax_pct),
      flow_tax_pct: toNumber(entity?.flow_tax_pct)
    }));
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Step header
// ---------------------------------------------------------------------------

function quoteRouteConfig(chain) {
  const route = chain || 'factory_trade_channel_entity';
  return {
    factory: ['factory_trade', 'factory_trade_channel_entity'].includes(route),
    trade: ['factory_trade', 'supplier_trade', 'trade_entity', 'trade_buyer', 'channel_reverse', 'factory_trade_channel_entity'].includes(route),
    channel: ['channel_reverse', 'factory_trade_channel_entity'].includes(route),
    entity: ['trade_entity', 'factory_trade_channel_entity'].includes(route),
    externalBuyer: route === 'trade_buyer'
  };
}

function buildBusinessChainPatch(form, nextChain) {
  const route = quoteRouteConfig(nextChain);
  const layerA = cloneLayerAData(form?.layer_a_data);
  const layerB = cloneLayerBData(form?.layer_b_data);
  const nextLayerA = { ...layerA, business_chain: nextChain };
  const nextLayerB = { ...layerB };
  if (!route.factory) {
    nextLayerA.use_factory_quote_as_cost = false;
    nextLayerB.trade_action_mode = 'cost_confirmed';
    nextLayerB.trade_cost_source = 'external_supplier';
    nextLayerB.trade_input_source = 'external_supplier';
    nextLayerB.trade_cost_basis_mode = 'manual';
  }
  return {
    business_chain: nextChain,
    layer_a_data: nextLayerA,
    layer_b_data: cloneLayerBData(nextLayerB)
  };
}

function isQuotePathAvailable(chain, hasFactoryLayer, hasTradeLayer, hasChannelLayer, hasEntityLayer) {
  const route = quoteRouteConfig(chain);
  if (route.factory && !hasFactoryLayer) return false;
  if (route.trade && !hasTradeLayer) return false;
  if (route.channel && !hasChannelLayer) return false;
  if (route.entity && !hasEntityLayer) return false;
  return true;
}

function buildSteps(hasFactoryLayer, hasTradeLayer, hasChannelLayer, hasEntityLayer, businessChain) {
  const route = quoteRouteConfig(businessChain);
  const steps = [];
  if (hasFactoryLayer && route.factory) {
    steps.push({ key: 'factory', label: 'A 工厂报价' });
  } else {
    steps.push({ key: 'lines', label: '商品行' });
  }
  if (hasTradeLayer && route.trade) steps.push({ key: 'trade', label: 'B 外贸报价' });
  if (hasChannelLayer && route.channel) steps.push({ key: 'channel', label: 'C 电商报价' });
  if (hasEntityLayer && route.entity) steps.push({ key: 'entity', label: 'E 主体利润' });
  return steps.map((step, idx) => ({ ...step, label: `${idx + 1}. ${step.label}` }));
}

function getCurrentPlanLabel(workspace) {
  const raw = workspace?.sku_name || workspace?.plan_name || workspace?.sku_code || workspace?.current_sku || '';
  if (!raw) return '当前套餐';
  const code = String(raw).toLowerCase();
  if (code === 'sku_a5') return 'A5 全栈旗舰版';
  if (code === 'sku_a4') return 'A4 工贸一体集团版';
  if (code === 'sku_a3') return 'A3 工贸一体跨境版';
  if (code === 'sku_a2') return 'A2 工贸一体版';
  if (code === 'sku_a1') return 'A1 工厂版';
  if (code === 'sku_b4') return 'B4 外贸跨境集团版';
  if (code === 'sku_b3') return 'B3 外贸集团版';
  if (code === 'sku_b2') return 'B2 外贸跨境版';
  if (code === 'sku_b1') return 'B1 外贸版';
  return raw;
}

function enabledCapabilityList(hasFactoryLayer, hasTradeLayer, hasChannelLayer, hasEntityLayer) {
  return [
    hasFactoryLayer ? ['A 工厂核算', '物料/BOM、工序、制造费用和工厂输出。'] : null,
    hasTradeLayer ? ['B 外贸报价', '采购成本、HS/退税、汇率、物流和 FOB/CIF/DDP。'] : null,
    hasChannelLayer ? ['C 电商渠道', '承接 B 的 DDP，测算平台费用、售价和渠道利润。'] : null,
    hasEntityLayer ? ['E 运营主体层', '承接 B/C 利润源，处理多主体利润和集团视图。'] : null
  ].filter(Boolean);
}

function QuoteLeftRail({
  collapsed,
  onToggleCollapsed,
  customerCollapsed,
  onToggleCustomer,
  capabilitiesCollapsed,
  onToggleCapabilities,
  routeOptionsCollapsed,
  onToggleRouteOptions,
  form,
  workspace,
  hasFactoryLayer,
  hasTradeLayer,
  hasChannelLayer,
  hasEntityLayer,
  selectedCustomer,
  onCustomerChange,
  onCustomerSelect,
  onSelectPath
}) {
  const layerA = cloneLayerAData(form.layer_a_data);
  const currentChain = form.business_chain || layerA.business_chain || 'factory_trade_channel_entity';
  const currentPath = QUOTE_PATH_OPTIONS.find((item) => item.chain === currentChain) || QUOTE_PATH_OPTIONS[0];
  const availablePathOptions = QUOTE_PATH_OPTIONS.filter((item) =>
    isQuotePathAvailable(item.chain, hasFactoryLayer, hasTradeLayer, hasChannelLayer, hasEntityLayer)
  );
  const customerName = selectedCustomer?.company_name || (form.customer_id ? `客户 #${form.customer_id}` : '未选择客户');
  const planLabel = getCurrentPlanLabel(workspace);
  const capabilities = enabledCapabilityList(hasFactoryLayer, hasTradeLayer, hasChannelLayer, hasEntityLayer);
  const capabilitySummary = capabilities.map(([title]) => title.split(' ')[0]).join(' / ') || '未启用';

  if (collapsed) {
    return (
      <aside className="sticky top-4 self-start rounded-xl border border-[#e7dfcf] bg-[#fffdf8] p-2 shadow-sm">
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#d8ccb8] bg-[#fffaf0] text-sm font-semibold text-slate-700 hover:bg-[#f5efe3]"
          onClick={onToggleCollapsed}
          aria-label="展开左侧栏"
          title="展开左侧栏"
        >
          ›
        </button>
        <div className="mt-4 flex min-h-[18rem] items-center justify-center">
          <div className="[writing-mode:vertical-rl] text-xs font-semibold tracking-normal text-slate-600">
            客户档案 / 报价路径
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="sticky top-4 max-h-[calc(100vh-2rem)] self-start overflow-y-auto rounded-xl border border-[#e7dfcf] bg-[#fffdf8] p-3 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-950">客户档案栏</div>
          <p className="mt-1 text-xs leading-snug text-slate-500">客户信息作为报价上下文，默认收起；需要维护客户资料时再展开。</p>
        </div>
        <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={onToggleCollapsed}>收起</button>
      </div>

      <section data-quote-panel="customer" className="rounded-lg border border-[#e7dfcf] bg-[#fffaf0] p-3">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 text-left"
          onClick={onToggleCustomer}
        >
          <span className="text-sm font-semibold text-slate-900">当前客户</span>
          <span className="text-xs text-slate-500">{customerCollapsed ? '展开' : '收起'}</span>
        </button>
        <div className="mt-2 rounded-md border border-[#e7dfcf] bg-[#fffdf8] p-2 text-xs leading-relaxed text-slate-700">
          <div className="font-semibold text-slate-950">{customerName}</div>
          <div className="mt-1 text-slate-500">
            {form.customer_id ? '客户已带入报价主单。' : '在报价主单里搜索并选择客户。'}
          </div>
        </div>
        {!customerCollapsed && (
          <div className="mt-3 space-y-2 text-xs text-slate-600">
            <div className="grid grid-cols-3 gap-2">
              <Link
                to="/app/customers/new"
                className="whitespace-nowrap rounded-md border border-[#d8ccb8] bg-white px-1.5 py-2 text-center text-[11px] font-semibold text-slate-700 hover:bg-[#fffaf0]"
              >
                新增客户
              </Link>
              <Link
                to="/app/customers/groups"
                className="whitespace-nowrap rounded-md border border-[#d8ccb8] bg-white px-1.5 py-2 text-center text-[11px] font-semibold text-slate-700 hover:bg-[#fffaf0]"
              >
                客户分组
              </Link>
              <Link
                to="/app/customers"
                className="whitespace-nowrap rounded-md border border-[#d8ccb8] bg-white px-1.5 py-2 text-center text-[11px] font-semibold text-slate-700 hover:bg-[#fffaf0]"
              >
                客户列表
              </Link>
            </div>
            <div className="rounded-md border border-[#e7dfcf] bg-white px-2 py-2">
              <CustomerPicker
                value={form.customer_id}
                selectedCustomer={selectedCustomer}
                onSelectCustomer={onCustomerSelect}
                onChange={onCustomerChange}
              />
            </div>
            <div className="rounded-md border border-[#e7dfcf] bg-white px-2 py-2">
              需要带走数据时由管理后台超级管理员处理并留痕。
            </div>
          </div>
        )}
      </section>

      <section data-quote-panel="path" className="mt-3 rounded-lg border border-[#e7dfcf] bg-[#fffaf0] p-3">
        <div className="flex w-full items-center justify-between gap-3 text-left">
          <span className="text-sm font-semibold text-slate-900">报价路径</span>
          <span className="text-xs text-slate-500">当前方案</span>
        </div>
        <div className="mt-2 rounded-md border border-[#e7dfcf] bg-[#fffdf8] p-2 text-xs leading-relaxed text-slate-700">
          <div className="font-semibold text-slate-950">{planLabel}</div>
          <div className="mt-1">{currentPath.title}</div>
        </div>

        <div data-quote-panel="capabilities" className="mt-3 rounded-md border border-[#e7dfcf] bg-white px-2 py-2">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 text-left"
            onClick={onToggleCapabilities}
          >
            <span>
              <span className="block text-xs font-semibold text-slate-900">已启用能力</span>
              <span className="mt-1 block text-[11px] leading-snug text-slate-500">{capabilitySummary}</span>
            </span>
            <span className="shrink-0 text-xs text-slate-500">{capabilitiesCollapsed ? '展开' : '收起'}</span>
          </button>
          {!capabilitiesCollapsed && (
            <div className="mt-2">
              <div className="space-y-2">
                {capabilities.map(([title, desc]) => (
                  <div key={title} className="rounded-md border border-[#e7dfcf] bg-white px-2 py-2">
                    <div className="text-xs font-semibold text-slate-950">{title}</div>
                    <div className="mt-1 text-[11px] leading-snug text-slate-500">{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div data-quote-panel="route-options" className="mt-3 rounded-md border border-[#e7dfcf] bg-white px-2 py-2">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 text-left"
            onClick={onToggleRouteOptions}
          >
            <span>
              <span className="block text-xs font-semibold text-slate-900">报价路径</span>
              <span className="mt-1 block text-[11px] leading-snug text-slate-500">{currentPath.title}</span>
            </span>
            <span className="shrink-0 text-xs text-slate-500">{routeOptionsCollapsed ? '展开' : '收起'}</span>
          </button>
          {!routeOptionsCollapsed && (
            <div className="mt-2">
              <div className="space-y-2">
                {availablePathOptions.map((item) => {
                  const active = item.chain === currentChain;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={[
                        'w-full rounded-md border px-2 py-2 text-left transition',
                        active
                          ? 'border-[#c9b58c] bg-[#f6ead4] text-slate-950 shadow-sm'
                          : 'border-[#e7dfcf] bg-white text-slate-700 hover:bg-[#fffaf0]'
                      ].join(' ')}
                      onClick={() => onSelectPath(item.chain)}
                    >
                      <span className="block text-xs font-semibold">{item.title}</span>
                      <span className="mt-1 block text-[11px] leading-snug text-slate-500">{item.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    </aside>
  );
}

function getNodeMeta(key, form, totals, formReadyForSave, previewOk) {
  const lineItems = Array.isArray(form.line_items) ? form.line_items : [];
  const linesOk = lineItems.every((li) =>
    li.sku && li.sku.trim().length > 0 && li.qty > 0 && li.unit_sell_rmb >= 0 && li.unit_cost_rmb >= 0
  );
  const fxRate = Number(form.fx_rate_to_rmb);
  const fxOk = form.currency === 'CNY' || (Number.isFinite(fxRate) && fxRate > 0 && form.fx_rate_confirmed);
  const meta = {
    basics: {
      kicker: '主单',
      title: '报价主单',
      input: '客户、市场、币种、数量、链路',
      output: '报价上下文与承接口径',
      status: form.customer_id != null && fxOk ? '主单就绪' : '资料待补',
      risk: fxOk ? '汇率已可用于试算' : '外币汇率需填写并确认'
    },
    factory: {
      kicker: '1 / A',
      title: '工厂报价',
      input: '自产 BOM、工序、制造费用或外采报价',
      output: 'EXW/FOB 成本与工厂利润',
      status: linesOk ? '可试算' : '明细待补',
      risk: '小批量上浮、生产明细和隐藏成本需留痕'
    },
    lines: {
      kicker: '1',
      title: '商品行',
      input: 'SKU、数量、售价、成本',
      output: '基础报价行与毛利试算',
      status: linesOk ? '可试算' : '明细待补',
      risk: '保存/提交前需要补齐 SKU、数量、售价和成本'
    },
    trade: {
      kicker: '2 / B',
      title: '外贸报价',
      input: '采购价、汇率、HS/退税、物流和关税',
      output: 'FOB/CIF/DDP 客户价',
      status: Number(totals?.trade_cost_rmb || 0) > 0 || Number(totals?.export_rebate_rmb || 0) !== 0 ? '已纳入试算' : '可进入',
      risk: 'HS、退税率、汇率和物流费需确认来源'
    },
    channel: {
      kicker: '3 / C',
      title: '电商报价',
      input: '承接 B 的 DDP/出口成本与平台参数',
      output: '终端价、渠道成本和渠道利润',
      status: Number(totals?.channel_cost_rmb || 0) > 0 ? '已纳入试算' : '可进入',
      risk: '平台扣点、广告率、退货率会影响终端利润'
    },
    entity: {
      kicker: '4 / E',
      title: '主体利润',
      input: '承接 B/C 利润源与主体税费',
      output: '国内/海外利润与集团净利',
      status: Number(totals?.entity_cost_rmb || 0) > 0 ? '已纳入试算' : '可进入',
      risk: '主体税率、费用分摊和结算路径需留痕'
    }
  };
  return meta[key] || {
    kicker: key,
    title: key,
    input: '手工输入',
    output: '报价试算',
    status: '可进入',
    risk: '保存/提交前统一校验'
  };
}

function StepHeader({ active, steps, onSelect, form, totals, formReadyForSave, previewOk }) {
  const moduleSteps = steps.filter((s) => s.key !== 'review');
  const activeIdx = moduleSteps.findIndex((s) => s.key === active);
  return (
    <section className="mb-5 rounded-xl border border-[#e7dfcf] bg-[#fffdf8] p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-slate-900">链路总览 / 模块入口</h2>
          <p className="mt-1 text-sm text-slate-500">点击任意报价节点，下方工作台直接切换到该模块；不锁死填写顺序。</p>
        </div>
        <div className="rounded-full border border-[#e7dfcf] bg-[#fffaf0] px-3 py-1 text-xs font-medium text-slate-600">
          报价主单固定在上方，A/B/C/E 节点独立进入
        </div>
      </div>
      <ol className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {moduleSteps.map((s, i) => {
        const cur  = i === activeIdx;
        const node = getNodeMeta(s.key, form, totals, formReadyForSave, previewOk);
        return (
          <li
            key={s.key}
            aria-current={cur ? 'step' : undefined}
            data-step-key={s.key}
            data-step-active={cur ? 'true' : 'false'}
          >
            <button
              type="button"
              className={[
                'flex h-full min-h-[10.5rem] w-full flex-col rounded-xl border p-3 text-left transition',
                cur
                  ? 'border-[#c9b58c] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d8c69d]'
                  : 'border-[#e7dfcf] bg-white text-slate-700 hover:border-[#d8c69d] hover:bg-[#fffaf0]'
              ].join(' ')}
              onClick={() => onSelect?.(s.key)}
            >
              <span className="text-[11px] font-semibold uppercase tracking-normal text-[#9a6a1d]">{node.kicker}</span>
              <span className="mt-1 text-sm font-semibold text-slate-950">{node.title}</span>
              <span className="mt-2 text-[11px] leading-snug text-slate-500">输入来源</span>
              <span className="text-xs font-medium leading-snug text-slate-700">{node.input}</span>
              <span className="mt-2 text-[11px] leading-snug text-slate-500">输出</span>
              <span className="text-xs font-medium leading-snug text-slate-700">{node.output}</span>
              <span className="mt-3 inline-flex w-fit rounded-full border border-[#e7dfcf] bg-[#fffdf8] px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                {node.status}
              </span>
              <span className="mt-2 line-clamp-2 text-[11px] leading-snug text-amber-700">{node.risk}</span>
            </button>
          </li>
        );
      })}
      </ol>
    </section>
  );
}

function getStageShellMeta(key) {
  const meta = {
    factory: {
      title: '工厂报价工作台',
      desc: '自产时用 BOM、工序、损耗、工厂费用计算；外采时直接录入供应商报价并保留来源。',
      badges: ['可自产', '可外采', '可反推'],
      tools: ['承接主单', '从下游反推', '确认工厂输出'],
      sources: [
        ['自产 BOM', '钓竿、鱼竿、配件等自有工厂成本。'],
        ['外部工厂报价', '服装等外部工厂给价，作为外贸基础成本。'],
        ['手工输入', '没有上游明细时允许直接录入。'],
        ['反推建议', '从售价或目标利润倒推出最大可接受成本。']
      ]
    },
    lines: {
      title: '商品行工作台',
      desc: '录入 SKU、数量、售价、成本和行追加费用，作为基础报价明细。',
      badges: ['手工输入', '可复制历史'],
      tools: ['承接主单', '复制历史报价', '确认商品行'],
      sources: [
        ['手工输入', '直接维护 SKU、数量、售价与成本。'],
        ['历史报价', '从旧报价复制后再调整。'],
        ['客户清单', '按客户给定清单补录。'],
        ['试算结果', '作为后续 B/C/E 承接基础。']
      ]
    },
    trade: {
      title: '外贸报价工作台',
      desc: '基于采购价、汇率、HS、退税、国际运费、保险、关税、清关、收款、融资、佣金和风险预提出 FOB/CIF/DDP。',
      badges: ['承接 A', '可外采', '需锁汇率'],
      tools: ['承接工厂输出', '锁定费用来源', '确认 B 输出'],
      sources: [
        ['A 工厂输出', '承接自产 EXW/FOB 或商品行成本。'],
        ['外部供应商', '供应商报价直接作为采购基础。'],
        ['HS/退税库', '自动匹配或手动维护退税率。'],
        ['物流报价', '国际运费、保险、清关、收款和资金费用等来源。']
      ]
    },
    channel: {
      title: '电商报价工作台',
      desc: 'C 是独立页面节点，输入承接 B 的出口/落地成本，测算平台费用、广告、FBA、售后、税费、结算、库存资金占用和渠道利润。',
      badges: ['承接 B', '可正算', '可反推'],
      tools: ['承接 B/DDP', '从售价反推', '确认 C 输出'],
      sources: [
        ['B 落地成本', '从外贸节点承接 DDP 或出口成本。'],
        ['平台参数', '佣金、支付费、广告费率。'],
        ['履约费用', 'FBA、海外仓、退货、售后和库存资金占用。'],
        ['反推建议', '从终端售价和目标利润反推成本上限。']
      ]
    },
    entity: {
      title: '运营主体层工作台',
      desc: '承接 B/C 利润源，处理境内外主体、费用分摊、税费和集团合并利润视图。',
      badges: ['承接 B/C', '集团视图', '需留痕'],
      tools: ['承接利润源', '校验税费路径', '确认 E 输出'],
      sources: [
        ['B 外贸利润', '出口主体利润与费用。'],
        ['C 渠道利润', '海外渠道或平台侧利润源。'],
        ['主体档案', '税率、费用、利润分配。'],
        ['结算路径', '平台、海外主体、国内主体之间的资金流。']
      ]
    }
  };
  return meta[key] || meta.factory;
}

function getNodeTabRows(stepKey, activeTab, form, totals) {
  const layerA = cloneLayerAData(form.layer_a_data);
  const layerB = cloneLayerBData(form.layer_b_data);
  const layerC = { ...DEFAULT_LAYER_C_DATA, ...(form.layer_c_data || {}) };
  const layerE = layerInputs(form.layer_e_data, DEFAULT_LAYER_E_DATA);
  const qty = totalQuoteQty(form.line_items);
  if (activeTab === 'carry') {
    const rows = {
      factory: [
        ['承接上游', '报价主单：客户、市场、币种、数量、业务链路'],
        ['输出给下游', `${layerA.output_term || 'EXW'} 工厂报价、物料/工序/制造费用明细`],
        ['当前数量', `${qty || 1} 件`]
      ],
      lines: [
        ['承接上游', '报价主单'],
        ['输出给下游', '商品行、售价、成本和毛利试算'],
        ['当前数量', `${qty || 1} 件`]
      ],
      trade: [
        ['承接上游', 'A 工厂报价或外部供应商报价'],
        ['输出给下游', 'FOB/CIF/DDP、退税额、外贸费用和资金费用'],
        ['HS 口径', layerB.hs_rebate_items.length > 0 ? `${layerB.hs_rebate_items.length} 个 HS 项` : (form.hs_code || '未填写')]
      ],
      channel: [
        ['承接上游', 'B 的 DDP / 海外落地成本'],
        ['输出给下游', '终端售价、渠道费用、库存资金占用和渠道利润率'],
        ['平台扣点', `${layerC.platform_commission_pct || 0}%`]
      ],
      entity: [
        ['承接上游', 'B/C 利润源'],
        ['输出给下游', '国内利润、海外利润、集团净利'],
        ['回款路径', layerE.settlement_path || '平台 → 海外主体 → 国内分红/服务费']
      ]
    };
    return rows[stepKey] || rows.factory;
  }
  if (activeTab === 'reverse') {
    return [
      ['反推原则', '只生成建议，不自动覆盖已确认的 BOM、采购价、DDP 或售价。'],
      ['可反推目标', stepKey === 'channel' ? '终端售价 → 最大 DDP / 最大采购价' : '目标利润 → 最大可接受成本'],
      ['当前建议', Number(totals?.gross_profit_rmb || 0) > 0 ? '当前试算已有利润空间，可继续细化。' : '当前试算不足，需补充价格、成本或费用。']
    ];
  }
  if (activeTab === 'trace') {
    return [
      ['数据来源', '手工输入、Excel/CSV 导入、产品目录、HS/退税库、汇率接口均需保留来源。'],
      ['计算口径', '界面金额按两位小数显示，后台连续计算不按显示值截断。'],
      ['提交校验', '跨节点可先进入填写；保存/提交时统一校验客户、汇率、报价行和试算错误。']
    ];
  }
  return [];
}

function NodeTabPanel({ rows }) {
  return (
    <div className="rounded-xl border border-[#e7dfcf] bg-[#fffdf8] p-4">
      <div className="grid gap-3">
        {rows.map(([label, value]) => (
          <div key={label} className="grid gap-2 rounded-lg border border-[#e7dfcf] bg-white px-3 py-2 md:grid-cols-[8rem_minmax(0,1fr)]">
            <div className="text-xs font-semibold text-slate-500">{label}</div>
            <div className="text-sm font-medium leading-relaxed text-slate-800">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const FACTORY_TOOL_ACTIONS = Object.freeze({
  '承接主单': { tab: 'carry', patch: { factory_action_mode: 'carry' } },
  '从下游反推': {
    tab: 'reverse',
    patch: { factory_action_mode: 'reverse', factory_input_mode: 'reverse', quote_logic: 'reverse' }
  },
  '确认工厂输出': { tab: 'forward', patch: { factory_action_mode: 'confirmed' } }
});

const FACTORY_SOURCE_ACTIONS = Object.freeze({
  '自产 BOM': { factory_input_mode: 'bom', factory_business_type: 'direct_export' },
  '外部工厂报价': { factory_input_mode: 'external_factory', factory_business_type: 'external_factory' },
  '手工输入': { factory_input_mode: 'manual' },
  '反推建议': { factory_input_mode: 'reverse', factory_action_mode: 'reverse', quote_logic: 'reverse' }
});

const LINE_SOURCE_ACTIONS = Object.freeze({
  '手工输入': { line_input_source: 'manual' },
  '历史报价': { line_input_source: 'history' },
  '客户清单': { line_input_source: 'customer_list' },
  '试算结果': { line_input_source: 'calculation' }
});

const TRADE_SOURCE_ACTIONS = Object.freeze({
  'A 工厂输出': {
    trade_cost_source: 'factory_output',
    trade_input_source: 'factory_output',
    trade_cost_basis_mode: 'carry_factory',
    useFactoryQuoteAsCost: true
  },
  '外部供应商': {
    trade_cost_source: 'external_supplier',
    trade_input_source: 'external_supplier',
    trade_cost_basis_mode: 'manual',
    useFactoryQuoteAsCost: false
  },
  'HS/退税库': { hs_rebate_source_enabled: true },
  '物流报价': { logistics_source_enabled: true }
});

const CHANNEL_SOURCE_ACTIONS = Object.freeze({
  'B 落地成本': { channel_input_source: 'trade_landed_cost' },
  '平台参数': { channel_input_source: 'platform_params' },
  '履约费用': { channel_input_source: 'fulfillment' },
  '反推建议': { channel_input_source: 'reverse' }
});

const ENTITY_SOURCE_ACTIONS = Object.freeze({
  'B 外贸利润': { entity_input_source: 'trade_profit' },
  'C 渠道利润': { entity_input_source: 'channel_profit' },
  '主体档案': { entity_input_source: 'entity_profile' },
  '结算路径': { entity_input_source: 'settlement_path' }
});

function visibleStageTools(stepKey, tools, businessChain) {
  const route = quoteRouteConfig(businessChain);
  if (stepKey === 'trade' && !route.factory) {
    return ['确认采购成本', '锁定费用来源', '确认 B 输出'];
  }
  return tools;
}

function visibleStageSources(stepKey, sources, businessChain) {
  const route = quoteRouteConfig(businessChain);
  if (stepKey !== 'trade') return sources;
  return sources.filter(([title]) => {
    if (title === 'A 工厂输出') return route.factory;
    if (title === '外部供应商') return !route.factory || businessChain === 'factory_trade_channel_entity';
    return true;
  });
}

function NodeWorkspaceShell({ stepKey, form, totals, formReadyForSave, previewOk, onPatch, children }) {
  const [activeTab, setActiveTab] = React.useState('forward');
  const node = getNodeMeta(stepKey, form, totals, formReadyForSave, previewOk);
  const stage = getStageShellMeta(stepKey);
  const layerA = cloneLayerAData(form.layer_a_data);
  const layerB = cloneLayerBData(form.layer_b_data);
  const layerC = layerInputs(form.layer_c_data, DEFAULT_LAYER_C_DATA);
  const layerE = layerInputs(form.layer_e_data, DEFAULT_LAYER_E_DATA);
  const currentChain = form.business_chain || layerA.business_chain || 'factory_trade_channel_entity';
  const currentRoute = quoteRouteConfig(currentChain);
  const tools = visibleStageTools(stepKey, stage.tools, currentChain);
  const sources = visibleStageSources(stepKey, stage.sources, currentChain);
  const tabs = [
    ['forward', '正算输入'],
    ['carry', '承接关系'],
    ['reverse', '反推结果'],
    ['trace', '留痕与说明']
  ];
  const patchLayerA = (nextPatch) => {
    if (typeof onPatch !== 'function') return;
    onPatch({ layer_a_data: { ...layerA, ...nextPatch } });
  };
  const patchLayerC = (nextPatch) => {
    if (typeof onPatch !== 'function') return;
    onPatch({ layer_c_data: { ...layerC, ...nextPatch } });
  };
  const patchLayerB = (nextPatch, layerAPatch = null) => {
    if (typeof onPatch !== 'function') return;
    const payload = { layer_b_data: cloneLayerBData({ ...layerB, ...nextPatch }) };
    if (layerAPatch) payload.layer_a_data = { ...layerA, ...layerAPatch };
    onPatch(payload);
  };
  const patchLayerE = (nextPatch) => {
    if (typeof onPatch !== 'function') return;
    onPatch({ layer_e_data: { ...layerE, ...nextPatch } });
  };
  const handleToolClick = (tool, idx) => {
    if (stepKey === 'factory' && FACTORY_TOOL_ACTIONS[tool]) {
      const action = FACTORY_TOOL_ACTIONS[tool];
      patchLayerA(action.patch);
      setActiveTab(action.tab);
      return;
    }
    if (stepKey === 'trade') {
      if (tool === '承接工厂输出') {
        if (!currentRoute.factory) {
          patchLayerB(
            {
              trade_action_mode: 'cost_confirmed',
              trade_cost_source: 'external_supplier',
              trade_input_source: 'external_supplier',
              trade_cost_basis_mode: 'manual'
            },
            { use_factory_quote_as_cost: false }
          );
          setActiveTab('forward');
          return;
        }
        patchLayerB(
          { trade_action_mode: 'carry', trade_cost_source: 'factory_output', trade_input_source: 'factory_output', trade_cost_basis_mode: 'carry_factory' },
          { use_factory_quote_as_cost: true }
        );
        setActiveTab('carry');
        return;
      }
      if (tool === '确认采购成本') {
        patchLayerB({ trade_action_mode: 'cost_confirmed' }, { use_factory_quote_as_cost: layerB.trade_cost_source === 'factory_output' });
        setActiveTab('forward');
        return;
      }
      if (tool === '锁定费用来源') {
        patchLayerB({ trade_action_mode: 'source_locked', trade_fee_source_locked: true });
        setActiveTab('trace');
        return;
      }
      if (tool === '确认 B 输出') {
        patchLayerB({ trade_action_mode: 'confirmed', trade_quote_mode: layerB.trade_quote_mode || 'line_sell' });
        setActiveTab('forward');
        return;
      }
    }
    if (stepKey === 'channel') {
      if (tool === '承接 B/DDP') {
        patchLayerC({ channel_action_mode: 'carry', channel_input_source: 'trade_landed_cost', cost_basis_mode: 'inherit_trade', channel_cost_basis_mode: 'carry_trade' });
        setActiveTab('carry');
        return;
      }
      if (tool.includes('反推')) {
        patchLayerC({ channel_action_mode: 'reverse', channel_input_source: 'reverse' });
        setActiveTab('reverse');
        return;
      }
      if (tool === '确认 C 输出') {
        patchLayerC({ channel_action_mode: 'confirmed' });
        setActiveTab('forward');
        return;
      }
    }
    if (stepKey === 'entity') {
      if (tool.includes('承接')) {
        patchLayerE({ entity_action_mode: 'carry' });
        setActiveTab('carry');
        return;
      }
      if (tool.includes('校验')) {
        patchLayerE({ entity_action_mode: 'tax_checked' });
        setActiveTab('trace');
        return;
      }
      if (tool.includes('确认')) {
        patchLayerE({ entity_action_mode: 'confirmed' });
        setActiveTab('forward');
        return;
      }
    }
    if (tool.includes('反推')) {
      setActiveTab('reverse');
      return;
    }
    if (tool.includes('承接')) {
      setActiveTab('carry');
      return;
    }
    if (tool.includes('确认') || idx === tools.length - 1) {
      setActiveTab('forward');
      return;
    }
    setActiveTab('trace');
  };
  const handleSourceClick = (title) => {
    if (stepKey === 'factory') {
      const nextPatch = FACTORY_SOURCE_ACTIONS[title];
      if (!nextPatch) return;
      patchLayerA(nextPatch);
      setActiveTab(title === '反推建议' ? 'reverse' : 'forward');
      return;
    }
    if (stepKey === 'lines') {
      const nextPatch = LINE_SOURCE_ACTIONS[title];
      if (!nextPatch) return;
      patchLayerA(nextPatch);
      setActiveTab(title === '试算结果' ? 'carry' : 'forward');
      return;
    }
    if (stepKey === 'trade') {
      const nextAction = TRADE_SOURCE_ACTIONS[title];
      if (!nextAction || typeof onPatch !== 'function') return;
      if (title === 'A 工厂输出' && !currentRoute.factory) {
        patchLayerB(
          {
            trade_action_mode: 'cost_confirmed',
            trade_cost_source: 'external_supplier',
            trade_input_source: 'external_supplier',
            trade_cost_basis_mode: 'manual'
          },
          { use_factory_quote_as_cost: false }
        );
        setActiveTab('forward');
        return;
      }
      const { useFactoryQuoteAsCost, ...tradePatch } = nextAction;
      const payload = { layer_b_data: cloneLayerBData({ ...layerB, ...tradePatch }) };
      if (typeof useFactoryQuoteAsCost === 'boolean') {
        payload.layer_a_data = { ...layerA, use_factory_quote_as_cost: useFactoryQuoteAsCost };
      }
      onPatch(payload);
      setActiveTab(title === 'A 工厂输出' ? 'carry' : 'forward');
      return;
    }
    if (stepKey === 'channel') {
      const nextPatch = CHANNEL_SOURCE_ACTIONS[title];
      if (!nextPatch) return;
      patchLayerC(nextPatch);
      setActiveTab(title === '反推建议' ? 'reverse' : 'forward');
      return;
    }
    if (stepKey === 'entity') {
      const nextPatch = ENTITY_SOURCE_ACTIONS[title];
      if (!nextPatch) return;
      patchLayerE(nextPatch);
      setActiveTab(title === '结算路径' ? 'trace' : 'forward');
    }
  };
  const isFactoryToolActive = (tool) => {
    if (stepKey !== 'factory') return false;
    const actionMode = FACTORY_TOOL_ACTIONS[tool]?.patch?.factory_action_mode;
    return Boolean(actionMode && layerA.factory_action_mode === actionMode);
  };
  const isToolActive = (tool) => {
    if (stepKey === 'factory') return isFactoryToolActive(tool);
    if (stepKey === 'trade') {
      if (tool === '承接工厂输出') {
        return currentRoute.factory && (layerB.trade_action_mode === 'carry' || layerB.trade_cost_source === 'factory_output');
      }
      if (tool === '确认采购成本') {
        return layerB.trade_action_mode === 'cost_confirmed'
          || (!currentRoute.factory && layerB.trade_cost_source !== 'factory_output');
      }
      if (tool === '锁定费用来源') return layerB.trade_fee_source_locked || layerB.trade_action_mode === 'source_locked';
      if (tool === '确认 B 输出') return layerB.trade_action_mode === 'confirmed';
    }
    if (stepKey === 'channel') {
      if (tool === '承接 B/DDP') return layerC.channel_action_mode === 'carry' || layerC.channel_input_source === 'trade_landed_cost';
      if (tool.includes('反推')) return layerC.channel_action_mode === 'reverse' || layerC.channel_input_source === 'reverse';
      if (tool === '确认 C 输出') return layerC.channel_action_mode === 'confirmed';
    }
    if (stepKey === 'entity') {
      if (tool.includes('承接')) return layerE.entity_action_mode === 'carry';
      if (tool.includes('校验')) return layerE.entity_action_mode === 'tax_checked';
      if (tool.includes('确认')) return layerE.entity_action_mode === 'confirmed';
    }
    return false;
  };
  const isSourceActive = (title) => {
    if (stepKey === 'factory') {
      return FACTORY_SOURCE_ACTIONS[title]?.factory_input_mode === layerA.factory_input_mode;
    }
    if (stepKey === 'lines') {
      return LINE_SOURCE_ACTIONS[title]?.line_input_source === layerA.line_input_source;
    }
    if (stepKey === 'trade') {
      const action = TRADE_SOURCE_ACTIONS[title];
      if (!action) return false;
      if (title === 'A 工厂输出') {
        return currentRoute.factory && (
          layerB.trade_cost_source === 'factory_output'
          || (!layerB.trade_cost_source && layerB.trade_cost_basis_mode === 'carry_factory')
        );
      }
      if (title === '外部供应商') {
        return layerB.trade_cost_source === 'external_supplier'
          || layerB.trade_input_source === 'external_supplier'
          || (!currentRoute.factory && layerB.trade_cost_source !== 'factory_output');
      }
      if (title === 'HS/退税库') return Boolean(layerB.hs_rebate_source_enabled);
      if (title === '物流报价') return Boolean(layerB.logistics_source_enabled);
      return layerB.trade_input_source === action.trade_input_source;
    }
    if (stepKey === 'channel') {
      return CHANNEL_SOURCE_ACTIONS[title]?.channel_input_source === layerC.channel_input_source;
    }
    if (stepKey === 'entity') {
      return ENTITY_SOURCE_ACTIONS[title]?.entity_input_source === layerE.entity_input_source;
    }
    return false;
  };

  return (
    <section className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)]">
        <div className="rounded-xl border border-[#e7dfcf] bg-[#fffdf8] p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-normal text-[#9a6a1d]">{node.kicker}</span>
            {stage.badges.map((badge) => (
              <span key={badge} className="rounded-full border border-[#e7dfcf] bg-[#fffaf0] px-2 py-0.5 text-[11px] font-medium text-slate-600">
                {badge}
              </span>
            ))}
          </div>
          <h3 className="mt-2 text-lg font-semibold text-slate-950">{node.title}</h3>
          <div className="mt-3 grid gap-2 text-sm">
            <div className="rounded-lg border border-[#e7dfcf] bg-white px-3 py-2">
              <span className="block text-[11px] text-slate-500">输入来源</span>
              <strong className="font-semibold text-slate-900">{node.input}</strong>
            </div>
            <div className="rounded-lg border border-[#e7dfcf] bg-white px-3 py-2">
              <span className="block text-[11px] text-slate-500">输出</span>
              <strong className="font-semibold text-slate-900">{node.output}</strong>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              <span className="block text-[11px]">状态 / 风险</span>
              <strong className="font-semibold">{node.status}</strong>
              <span className="ml-2 text-xs">{node.risk}</span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[#e7dfcf] bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">操作按钮 / 输入方式</h2>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500">{stage.desc}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {tools.map((tool, idx) => {
                const active = isToolActive(tool);
                return (
                  <button
                    key={tool}
                    type="button"
                    onClick={() => handleToolClick(tool, idx)}
                    className={[
                      'rounded-md border px-3 py-2 text-xs font-semibold transition',
                      active
                        ? 'border-[#a97416] bg-[#f8d48a] text-slate-950 shadow-sm ring-1 ring-[#d69a2d]'
                        : idx === stage.tools.length - 1
                          ? 'border-[#c9b58c] bg-[#f6ead4] text-slate-950 hover:bg-[#efe0c5]'
                          : 'border-[#e7dfcf] bg-[#fffdf8] text-slate-700 hover:border-[#d8c69d] hover:bg-[#fffaf0]'
                    ].join(' ')}
                  >
                    {tool}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {sources.map(([title, desc]) => {
              const active = isSourceActive(title);
              const sourceClassName = [
                'rounded-lg border px-3 py-2 text-left transition cursor-pointer hover:border-[#d8c69d] hover:bg-[#fffaf0]',
                active
                  ? 'border-[#a97416] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d69a2d]'
                  : 'border-[#e7dfcf] bg-[#fffdf8]'
              ].join(' ');

              return (
                <button
                  key={title}
                  type="button"
                  aria-pressed={active}
                  onClick={() => handleSourceClick(title)}
                  className={sourceClassName}
                >
                  <b className="text-sm text-slate-900">{title}</b>
                  <span className="mt-1 block text-xs leading-snug text-slate-500">{desc}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap gap-2 border-t border-[#eee5d6] pt-3">
            {tabs.map(([value, label]) => {
              const active = activeTab === value;
              return (
                <button
                  key={value}
                  type="button"
                  className={[
                    'rounded-full border px-3 py-1 text-xs font-semibold transition',
                    active
                      ? 'border-[#c9b58c] bg-[#f6ead4] text-slate-950'
                      : 'border-[#e7dfcf] bg-[#fffdf8] text-slate-600 hover:bg-[#fffaf0]'
                  ].join(' ')}
                  onClick={() => setActiveTab(value)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {activeTab === 'forward' ? (
        <div className="rounded-xl border border-[#e7dfcf] bg-[#fffdf8] p-4">
          {children}
        </div>
      ) : (
        <NodeTabPanel rows={getNodeTabRows(stepKey, activeTab, form, totals)} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Customer picker (light — typeahead pulled from /api/customers)
// ---------------------------------------------------------------------------

function CustomerPicker({ value, onChange, selectedCustomer = null, onSelectCustomer = null }) {
  const [q, setQ]   = useState('');
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [err, setErr]     = useState(null);
  const [selected, setSelected] = useState(null);
  const displayCustomer = selectedCustomer || selected;

  useEffect(() => {
    let cancelled = false;
    const keyword = q.trim();
    if (keyword.length < 2) {
      setItems([]);
      setSearching(false);
      setErr(null);
      return () => { cancelled = true; };
    }
    const handle = setTimeout(async () => {
      try {
        setSearching(true);
        const query = new URLSearchParams({ limit: '10', q: keyword });
        const res = await api.get(`/api/customers?${query.toString()}`);
        if (cancelled) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
        setErr(null);
      } catch (e) {
        if (!cancelled) setErr(e.message || '搜索客户失败');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [q]);

  useEffect(() => {
    if (value == null) {
      setSelected(null);
      return;
    }

    const valueId = Number(value);
    if (selectedCustomer && Number(selectedCustomer.id) === valueId) {
      setSelected(selectedCustomer);
      return;
    }

    const existing = items.find((c) => Number(c.id) === valueId);
    if (existing) {
      setSelected(existing);
      onSelectCustomer?.(existing);
      return;
    }

    if (selected && Number(selected.id) === valueId) return;

    let cancelled = false;
    (async () => {
      try {
        const customer = await api.get(`/api/customers/${valueId}`);
        if (cancelled) return;
        setSelected(customer);
        onSelectCustomer?.(customer);
      } catch (e) {
        if (!cancelled) {
          setSelected({ id: valueId, company_name: `客户 #${valueId}` });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [value, items, selected, selectedCustomer, onSelectCustomer]);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3">
        <label className="label mb-0">报价客户</label>
        <div className="flex shrink-0 items-center gap-2 text-xs">
          <Link to="/app/customers/new" className="text-chango-700 hover:underline">
            新增客户
          </Link>
          <span className="text-slate-300">/</span>
          <Link to="/app/customers/groups" className="text-chango-700 hover:underline">
            客户分组
          </Link>
        </div>
      </div>
      <input
        type="search"
        className="field"
        placeholder="输入至少 2 个字搜索客户…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {err && <p className="err">{err}</p>}
      {q.trim().length > 0 && q.trim().length < 2 && (
        <p className="mt-1 text-xs text-slate-400">继续输入，公司名、联系人、邮箱或电话都可以搜。</p>
      )}
      {q.trim().length >= 2 && (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm">
          {searching && (
            <p className="px-3 py-2 text-xs text-slate-400">搜索中…</p>
          )}
          {!searching && items.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-400">未找到匹配客户，可点击上方新增客户。</p>
          )}
          {!searching && items.map((c) => {
            const active = c.id === value;
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => {
                  setSelected(c);
                  setQ('');
                  setItems([]);
                  onSelectCustomer?.(c);
                  onChange(c.id, c);
                }}
                className={[
                  'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm',
                  active ? 'bg-chango-50 text-chango-700' : 'hover:bg-slate-50'
                ].join(' ')}
              >
                <span className="min-w-0 truncate">{c.company_name || `#${c.id}`}</span>
                <span className="shrink-0 text-xs text-slate-400">{c.country_code || ''}</span>
              </button>
            );
          })}
        </div>
      )}
      {displayCustomer && (
        <p className="hint">
          已选 · <strong>{displayCustomer.company_name || `客户 #${displayCustomer.id}`}</strong>
          <button
            type="button"
            className="ml-2 text-chango-700 underline"
            onClick={() => {
              setSelected(null);
              onSelectCustomer?.(null);
              onChange(null, null);
            }}
          >
            取消
          </button>
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FX Rate Component with Auto/Manual Toggle + Deviation Warning
// ---------------------------------------------------------------------------

/**
 * HsCodeField (Task #117) — HS 编码 + 自动查退税率
 *
 * 用户输 HS 编码 → 防抖 300ms → 调 /api/hs-rebate/lookup 自动填 tax_rebate_rate
 * 显示退税率信息（来源 + 描述），用户可手动覆盖
 *
 * 业务背景：外贸毛利核心 = 销售额 - 成本 + 退税额（销售额 × 退税率）
 * 不同 HS 编码退税率不同（13%/9%/6%/0%），错填亏大
 */
function deriveHsSearchKeyword(lineItems = []) {
  const firstNamedLine = lineItems.find((item) => String(item?.description || '').trim());
  if (firstNamedLine) return String(firstNamedLine.description).trim().slice(0, 30);
  const firstSkuLine = lineItems.find((item) => String(item?.sku || '').trim());
  if (firstSkuLine) return String(firstSkuLine.sku).trim().slice(0, 30);
  return '';
}

function normalizeHsSearchResults(res) {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  if (Array.isArray(res)) return res;
  return [];
}

function HsCodeField({ value, rebateRate, onPatch, enabled = true, lineItems = [] }) {
  const [hsInput, setHsInput] = React.useState(value || '');
  const [info, setInfo] = React.useState(null);
  const [searchKw, setSearchKw] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [lookupLoading, setLookupLoading] = React.useState(false);
  const [lookupError, setLookupError] = React.useState(null);

  React.useEffect(() => {
    setHsInput(value || '');
  }, [value]);

  const applyHsItem = (item) => {
    if (!item) return;
    setHsInput(item.hs_code);
    setInfo(item);
    setLookupError(null);
    setSearchKw('');
    setSearchResults([]);
    onPatch({ hs_code: item.hs_code, tax_rebate_rate: item.rebate_rate, tax_rebate_source: 'auto' });
  };

  const rebatePctValue = rebateRate == null || rebateRate === ''
    ? ''
    : String(roundPercent(Number(rebateRate) * 100));

  const handleManualRebateChange = (nextValue) => {
    if (nextValue === '') {
      onPatch({ tax_rebate_rate: null, tax_rebate_source: null });
      return;
    }
    const pct = Number(nextValue);
    if (!Number.isFinite(pct)) return;
    const bounded = Math.min(100, Math.max(-100, pct));
    onPatch({ tax_rebate_rate: bounded / 100, tax_rebate_source: 'manual' });
  };

  // 防抖查询 HS 退税率
  React.useEffect(() => {
    if (!enabled) {
      setInfo(null);
      setLookupError(null);
      return;
    }
    if (!hsInput || hsInput.length < 4) {
      setInfo(null);
      setLookupError(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setLookupLoading(true);
        setLookupError(null);
        const res = await api.get(`/api/hs-rebate/lookup?hs_code=${encodeURIComponent(hsInput)}`);
        const item = res?.data || res;
        if (item && item.rebate_rate != null) {
          setInfo(item);
          onPatch({ tax_rebate_rate: item.rebate_rate, tax_rebate_source: 'auto' });
        } else {
          setInfo(null);
          setLookupError('字典暂未收录这个 HS 编码，可用商品名称搜索匹配。');
        }
      } catch (err) {
        setInfo(null);
        const status = err?.status || err?.response?.status;
        setLookupError(status === 404
          ? '字典暂未收录这个 HS 编码，可用商品名称搜索匹配。'
          : 'HS 编码查询失败，请稍后重试或改用品类搜索。');
      } finally {
        setLookupLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [enabled, hsInput]);

  // 关键字搜索
  React.useEffect(() => {
    if (!enabled) {
      setSearchResults([]);
      return;
    }
    if (!searchKw || searchKw.length < 2) {
      setSearchResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const res = await api.get(`/api/hs-rebate/search?q=${encodeURIComponent(searchKw)}&limit=10`);
        setSearchResults(normalizeHsSearchResults(res));
        setLookupError(null);
      } catch {
        setSearchResults([]);
        setLookupError('商品大类/品名搜索失败，请稍后重试。');
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [enabled, searchKw]);

  const handleAutoMatchFromLine = async () => {
    const keyword = String(searchKw || '').trim()
      || deriveHsSearchKeyword(lineItems)
      || String(hsInput || '').trim();
    if (!keyword) {
      setLookupError('输入商品大类、品名或 HS 编码后再搜索候选编码。');
      return;
    }
    setSearchKw(keyword);
    setLookupLoading(true);
    setLookupError(null);
    try {
      const res = await api.get(`/api/hs-rebate/search?q=${encodeURIComponent(keyword)}&limit=10`);
      const list = normalizeHsSearchResults(res);
      setSearchResults(list);
      if (list.length === 0) {
        setLookupError(`没有按“${keyword}”匹配到 HS 编码，请换一个商品大类或品名。`);
      }
    } catch {
      setLookupError('搜索 HS 候选编码失败，请稍后重试或手动填写。');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleHsInputChange = (nextValue) => {
    setHsInput(nextValue);
    setInfo(null);
    setLookupError(null);
    onPatch({ hs_code: nextValue, tax_rebate_rate: null, tax_rebate_source: null });
  };

  const handlePickFromSearch = (item) => {
    applyHsItem(item);
  };

  if (!enabled) {
    return (
      <div>
        <label className="label">HS 编码 + 退税率</label>
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
          当前套餐未开通外贸模块，HS 编码字典和出口退税率不参与本报价。
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label className="label mb-0">HS 编码 + 退税率</label>
        <button
          type="button"
          className="text-xs text-chango-700 underline"
          onClick={handleAutoMatchFromLine}
        >
          搜索 HS 候选
        </button>
      </div>
      <input
        type="text"
        className="field"
        value={hsInput}
        onChange={(e) => handleHsInputChange(e.target.value)}
        placeholder="如 8517120000（10-13 位数字）"
      />
      <p className="hint mt-1">
        HS 不绑定 SKU；可按商品大类、品名或 HS 编码搜索候选，再手动选中。
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <label className="block">
          <span className="label">退税率（%）</span>
          <input
            type="number"
            className="field text-right tabular-nums"
            min="-100"
            max="100"
            step="0.01"
            value={rebatePctValue}
            onChange={(e) => handleManualRebateChange(e.target.value)}
            placeholder="查不到时可手动填写，如 13；特殊税费可填 -13"
          />
        </label>
        <div className="self-end">
          <span className="label">常用</span>
          <div className="flex gap-1">
            {[13, 9, 6, 0, -13].map((pct) => (
              <button
                key={pct}
                type="button"
                className="rounded border border-slate-200 px-2 py-2 text-xs text-slate-700 hover:bg-slate-50"
                onClick={() => handleManualRebateChange(String(pct))}
              >
                {pct}%
              </button>
            ))}
          </div>
        </div>
      </div>
      {lookupLoading && (
        <p className="hint mt-1">正在查询 HS 编码...</p>
      )}
      {info && (
        <p className="hint mt-1 text-emerald-600">
          {info.description} · 退税率 <strong>{(Number(info.rebate_rate) * 100).toFixed(0)}%</strong>
          <span className="text-slate-400"> · 来源 {info.data_source || info.source || '商品代码库'}</span>
        </p>
      )}
      {lookupError && (
        <p className="hint mt-1 text-yellow-700">{lookupError}</p>
      )}
      <input
        type="text"
        className="field mt-2"
        value={searchKw}
        onChange={(e) => setSearchKw(e.target.value)}
        placeholder="输入商品大类/品名/HS 编码，如：钓鱼竿、陶瓷餐具、服装、9507"
      />
      {searchResults.length > 0 && (
        <ul className="mt-1 max-h-48 overflow-auto border border-slate-200 rounded text-sm">
          {searchResults.map((item) => {
            const label = item.description || item.goods_name || item.category || '未命名商品';
            return (
              <li key={item.hs_code}
                  className="px-2 py-1 hover:bg-slate-100 cursor-pointer"
                  onClick={() => handlePickFromSearch(item)}>
                <code className="text-slate-500">{item.hs_code}</code> · {label}
                {item.category ? <span className="ml-1 text-xs text-slate-400">[{item.category}]</span> : null}
                <span className="float-right text-emerald-600">{(Number(item.rebate_rate) * 100).toFixed(0)}%</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FxRateField({
  currency,
  value,
  confirmed = false,
  onPatch,
  autoRate,
  source,
  fetchedAt,
  rateDate,
  loading = false,
  error = null,
  onRetry
}) {
  const isCNY = currency === 'CNY';
  const [fxMode, setFxMode] = React.useState('auto');
  const [manualValue, setManualValue] = React.useState(value || '');
  const lastAppliedAutoRef = React.useRef(null);
  const previousCurrencyRef = React.useRef(currency);

  React.useEffect(() => {
    if (previousCurrencyRef.current === currency) return;
    previousCurrencyRef.current = currency;
    lastAppliedAutoRef.current = null;
    if (currency !== 'CNY') {
      setFxMode('auto');
      setManualValue('');
      onPatch({ fx_rate_to_rmb: '', fx_rate_confirmed: false });
    }
  }, [currency, onPatch]);

  React.useEffect(() => {
    if (isCNY) {
      setFxMode('auto');
      setManualValue(1);
      lastAppliedAutoRef.current = 'CNY:1';
      return;
    }
    if (fxMode === 'manual') {
      setManualValue(value || '');
    }
  }, [currency, value, confirmed, isCNY, fxMode]);

  React.useEffect(() => {
    if (!isCNY && error) {
      setFxMode('manual');
    }
  }, [error, isCNY]);

  React.useEffect(() => {
    if (isCNY || fxMode !== 'auto' || !autoRate || source === 'identity') return;
    const nextRate = Number(autoRate);
    if (!Number.isFinite(nextRate) || nextRate <= 0) return;

    const applyKey = `${currency}:${nextRate}`;
    if (lastAppliedAutoRef.current === applyKey && Number(value) === nextRate) return;
    lastAppliedAutoRef.current = applyKey;
    setManualValue(nextRate);
    if (Number(value) !== nextRate) {
      onPatch({ fx_rate_to_rmb: nextRate, fx_rate_confirmed: true });
    } else if (!confirmed) {
      onPatch({ fx_rate_confirmed: true });
    }
  }, [currency, isCNY, fxMode, autoRate, source, value, confirmed]);

  // Compute deviation percentage if in manual mode with an auto rate available
  let deviationPct = null;
  let deviationColor = 'text-slate-400'; // default grey
  if (fxMode === 'manual' && autoRate && manualValue && !isCNY) {
    const manual = Number(manualValue);
    if (Number.isFinite(manual) && manual > 0) {
      deviationPct = Math.abs(manual - autoRate) / autoRate * 100;
      if (deviationPct < 1) {
        deviationColor = 'text-slate-400'; // grey < 1%
      } else if (deviationPct < 3) {
        deviationColor = 'text-yellow-600'; // yellow 1-3%
      } else {
        deviationColor = 'text-red-600'; // red >= 3%
      }
    }
  }

  const handleModeChange = (mode) => {
    setFxMode(mode);
    if (mode === 'auto') {
      if (autoRate) {
        onPatch({ fx_rate_to_rmb: Number(autoRate), fx_rate_confirmed: true });
        setManualValue(Number(autoRate));
      } else if (!isCNY) {
        onPatch({ fx_rate_confirmed: false });
        onRetry?.();
      }
    } else if (!isCNY) {
      onPatch({ fx_rate_confirmed: false });
    }
  };

  const handleManualChange = (e) => {
    const val = e.target.value;
    setManualValue(val);
    if (!val) {
      onPatch({ fx_rate_to_rmb: '', fx_rate_confirmed: false });
      return;
    }
    if (val && autoRate) {
      const manual = Number(val);
      if (Number.isFinite(manual) && manual > 0) {
        const dev = Math.abs(manual - autoRate) / autoRate * 100;
        // §ST.14 + UEDA 拍板：偏差 ≥ 3% 强制二次确认（防客户输错汇率亏钱）
        if (dev >= 3) {
          const ok = window.confirm(
            `汇率偏差 ${dev.toFixed(1)}%（实时 ${autoRate.toFixed(4)} vs 你输入 ${manual.toFixed(4)}）\n\n` +
            `确定按你输入的 ${manual.toFixed(4)} 计算吗？\n\n` +
            `（取消则保留上一次的有效汇率）`
          );
          if (!ok) {
            // 用户取消 → 恢复输入框（不更新 onPatch）
            setManualValue(value || autoRate || '');
            return;
          }
        }
      }
    }
    onPatch({ fx_rate_to_rmb: toNumber(val), fx_rate_confirmed: false });
  };

  const manualNumber = Number(manualValue);
  const hasManualRate = !isCNY && Number.isFinite(manualNumber) && manualNumber > 0;

  return (
    <div>
      <label className="label">汇率 (1 {currency} = ? RMB)</label>
      {!isCNY && (
        <div className="mb-2 flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="fx-mode"
              value="auto"
              checked={fxMode === 'auto'}
              onChange={(e) => handleModeChange(e.target.value)}
            />
            自动获取
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="fx-mode"
              value="manual"
              checked={fxMode === 'manual'}
              onChange={(e) => handleModeChange(e.target.value)}
            />
            手动输入
          </label>
        </div>
      )}

      {fxMode === 'auto' && !isCNY ? (
        <div>
          <div className="field flex items-center justify-between gap-2 bg-slate-50 px-3 py-2 text-slate-600">
            <span className="font-medium">
              {loading ? '正在获取...' : (autoRate?.toFixed(6) || '暂未获取')}
            </span>
            {fetchedAt && (
              <span className="text-xs text-slate-400">
                ({rateDate || new Date(fetchedAt).toLocaleTimeString('zh-CN')})
              </span>
            )}
          </div>
          {error ? (
            <p className="err mt-1">
              {error}
              <button
                type="button"
                className="ml-2 text-chango-700 underline"
                onClick={onRetry}
              >
                重新获取
              </button>
            </p>
          ) : (
            <p className="hint mt-1">
              汇率获取成功后会自动写入报价计算{source ? `，来源：${source}` : ''}。
            </p>
          )}
        </div>
      ) : (
        <div>
          {!isCNY && error && (
            <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <div className="font-medium">自动汇率获取失败，已切到手动输入。</div>
              <div className="mt-1">
                请填写 1 {currency} 兑换人民币的汇率并勾选确认，保存/提交前需要完成。
                <button
                  type="button"
                  className="ml-2 font-medium text-amber-900 underline"
                  onClick={onRetry}
                >
                  重新获取
                </button>
              </div>
            </div>
          )}
          <input
            type="number"
            step="0.000001"
            min="0.0001"
            disabled={isCNY}
            className="field"
            value={isCNY ? 1 : manualValue}
            onChange={handleManualChange}
            placeholder={autoRate?.toFixed(4) || ''}
          />
          {fxMode === 'manual' && !isCNY && autoRate && deviationPct !== null && (
            <p className={`hint mt-1 ${deviationColor}`}>
              当前实时汇率: {autoRate.toFixed(6)} • 你输入: {manualValue} •
              偏差 {deviationPct.toFixed(2)}%
              {deviationPct >= 3 && ' ⚠️ 确认按你输入的汇率计算'}
            </p>
          )}
          {fxMode === 'manual' && !isCNY && !autoRate && !error && (
            <p className="hint mt-1">请输入汇率；系统不会用 1.0000 代替外币汇率。</p>
          )}
          {fxMode === 'manual' && !isCNY && (
            <label className="mt-2 flex items-start gap-2 rounded-lg border border-[#e7dfcf] bg-[#fffaf0] px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(confirmed)}
                disabled={!hasManualRate}
                onChange={(e) => onPatch({ fx_rate_confirmed: e.target.checked })}
              />
              <span>
                我确认按 {hasManualRate ? Number(manualValue).toFixed(6) : '手动填写的'} 汇率计算本报价
              </span>
            </label>
          )}
          {fxMode === 'manual' && !isCNY && hasManualRate && !confirmed && (
            <p className="err mt-1">保存/提交前需要勾选确认汇率。</p>
          )}
          {isCNY && <p className="hint">人民币报价固定为 1。</p>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Basics
// ---------------------------------------------------------------------------

function StepBasics({ form, onPatch, selectedCustomer, onCustomerSelect }) {
  const isCNY = form.currency === 'CNY';
  const layerA = cloneLayerAData(form.layer_a_data);
  const quoteQty = totalQuoteQty(form.line_items);
  const businessChain = form.business_chain || layerA.business_chain || 'factory_trade_channel_entity';
  const chainLabel = BUSINESS_CHAIN_OPTIONS.find(([value]) => value === businessChain)?.[1] || BUSINESS_CHAIN_OPTIONS[0][1];
  const scenarioSummary = [
    `业务链路：${chainLabel}`,
    `市场：${layerA.target_market || '未填写'}`,
    `币种：${CURRENCY_LABEL[form.currency] || form.currency}`,
    `数量：${quoteQty || 1} 件`,
    `逻辑：${QUOTE_LOGIC_OPTIONS.find(([value]) => value === layerA.quote_logic)?.[1] || '正算'}`
  ].join(' · ');
  const [autoRate, setAutoRate] = React.useState(null);
  const [fxSource, setFxSource] = React.useState(null);
  const [fetchedAt, setFetchedAt] = React.useState(null);
  const [rateDate, setRateDate] = React.useState(null);
  const [fxLoading, setFxLoading] = React.useState(false);
  const [fxError, setFxError] = React.useState(null);
  const [fxReloadSeq, setFxReloadSeq] = React.useState(0);
  const patchLayerA = (patch) => {
    onPatch({ layer_a_data: { ...cloneLayerAData(form.layer_a_data), ...patch } });
  };
  const patchQuoteQty = (value) => {
    const nextQty = Math.max(1, Math.round(toNumber(value)));
    const lines = Array.isArray(form.line_items) && form.line_items.length > 0
      ? form.line_items
      : [emptyLine()];
    const otherQty = lines.slice(1).reduce((acc, item) => acc + Math.max(1, Math.round(toNumber(item?.qty))), 0);
    const firstQty = Math.max(1, nextQty - otherQty);
    onPatch({
      line_items: lines.map((line, idx) => (
        idx === 0 ? { ...line, qty: firstQty } : line
      ))
    });
  };

  // Fetch auto rate when currency changes
  React.useEffect(() => {
    if (isCNY) {
      setAutoRate(1);
      setFxSource('identity');
      setFetchedAt(new Date().toISOString());
      setRateDate(null);
      setFxLoading(false);
      setFxError(null);
      return;
    }

    let cancelled = false;
    const fetchRate = async () => {
      try {
        setFxLoading(true);
        setFxError(null);
        setAutoRate(null);
        setFxSource(null);
        setFetchedAt(null);
        setRateDate(null);
        const res = await api.get(`/api/fx-rate/current?from=${form.currency}&to=CNY`);
        if (!cancelled && res.rate) {
          setAutoRate(Number(res.rate));
          setFxSource(res.source || null);
          setFetchedAt(res.fetched_at);
          setRateDate(res.rate_date || null);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[FX] fetch failed:', err.message);
          setAutoRate(null);
          setFxSource(null);
          setRateDate(null);
          setFxError('自动汇率暂不可用，请重新获取或切到手动输入。');
        }
      } finally {
        if (!cancelled) setFxLoading(false);
      }
    };

    fetchRate();
    return () => { cancelled = true; };
  }, [form.currency, isCNY, fxReloadSeq]);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <CustomerPicker
        value={form.customer_id}
        selectedCustomer={selectedCustomer}
        onSelectCustomer={onCustomerSelect}
        onChange={(id, customer) => {
          onCustomerSelect?.(customer);
          onPatch({ customer_id: id });
        }}
      />
      <div>
        <label className="label">目标市场</label>
        <input
          type="text"
          className="field"
          value={form.layer_a_data?.target_market || ''}
          onChange={(e) => patchLayerA({ target_market: e.target.value })}
          placeholder="如 日本、美国、欧盟"
          maxLength={80}
        />
        <p className="hint">用于驾驶舱和报价口径说明。</p>
      </div>
      <div>
        <label className="label">币种</label>
        <select
          className="field"
          value={form.currency}
          onChange={(e) => {
            const nextCurrency = e.target.value;
            onPatch({
              currency: nextCurrency,
              fx_rate_to_rmb: nextCurrency === 'CNY' ? 1 : '',
              fx_rate_confirmed: nextCurrency === 'CNY'
            });
          }}
        >
          {SUPPORTED_CURRENCIES.map((c) => (
            <option key={c} value={c}>{CURRENCY_LABEL[c]}</option>
          ))}
        </select>
        <p className="hint">报价最终以人民币落账；外币仅用于展示换算。</p>
      </div>
      <FxRateField
        currency={form.currency}
        value={form.fx_rate_to_rmb}
        confirmed={form.fx_rate_confirmed}
        onPatch={onPatch}
        autoRate={autoRate}
        source={fxSource}
        fetchedAt={fetchedAt}
        rateDate={rateDate}
        loading={fxLoading}
        error={fxError}
        onRetry={() => setFxReloadSeq((seq) => seq + 1)}
      />
      <div className="md:col-span-2">
        <label className="block">
          <span className="label">报价数量</span>
          <input
            type="number"
            min="1"
            step="1"
            className="field text-right tabular-nums"
            value={quoteQty || 1}
            onChange={(e) => patchQuoteQty(e.target.value)}
          />
        </label>
      </div>
      <div>
        <label className="label">业务链路</label>
        <select
          className="field"
          value={businessChain}
          onChange={(e) => {
            const nextChain = e.target.value;
            onPatch(buildBusinessChainPatch(form, nextChain));
          }}
        >
          {BUSINESS_CHAIN_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <p className="hint">决定主链路说明，不改变 A/B/C/E 的计算字段。</p>
      </div>
      <div>
        <label className="label">场景摘要</label>
        <div className="min-h-[42px] rounded-md border border-[#e7dfcf] bg-[#fffaf0] px-3 py-2 text-sm leading-snug text-slate-700">
          {scenarioSummary}
        </div>
      </div>
      <div className="md:col-span-2">
        <span className="label">报价逻辑</span>
        <div className="grid gap-2 sm:grid-cols-3">
          {QUOTE_LOGIC_OPTIONS.map(([value, label]) => {
            const active = layerA.quote_logic === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                className={[
                  'rounded-lg border px-4 py-3 text-sm font-semibold transition',
                  active
                    ? 'border-[#c9b58c] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d8c69d]'
                    : 'border-[#e7dfcf] bg-[#fffdf8] text-slate-700 hover:border-[#d8c69d] hover:bg-[#f5efe3]'
                ].join(' ')}
                onClick={() => patchLayerA({ quote_logic: value })}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="label">报价单语言</label>
        <select
          className="field"
          value={form.lang || 'zh'}
          onChange={(e) => onPatch({ lang: e.target.value })}
        >
          <option value="zh">中文</option>
          <option value="en">英文</option>
          <option value="bilingual">中英对照</option>
        </select>
        <p className="hint">选择 PDF 导出时的语言模式。</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Lines
// ---------------------------------------------------------------------------

function FactoryResultRow({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 break-words text-base font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

function StepFactoryQuote({ form, onPatch, previewLines, totals }) {
  const layerA = cloneLayerAData(form.layer_a_data);
  const summary = totals?.layer_a_data?.summary || {};
  const factoryFileRef = React.useRef(null);
  const [factoryMessage, setFactoryMessage] = React.useState(null);
  const quoteQty = Math.max(1, totalQuoteQty(form.line_items));
  const manufacturingFeeMode = normalizeManufacturingFeeMode(
    layerA.manufacturing_fee_mode,
    DEFAULT_LAYER_A_DATA.manufacturing_fee_mode
  );
  const materialAmountPerUnit = layerA.material_items.reduce(
    (acc, row) => acc + toNumber(row.consumption) * toNumber(row.tax_included_unit_price_rmb),
    0
  );
  const processAmountPerUnit = layerA.process_items.reduce(
    (acc, row) => acc + toNumber(row.standard_hours) * toNumber(row.labor_rate_rmb_per_hour) + toNumber(row.equipment_energy_rmb),
    0
  );
  const materialRatioBase = Number(summary.material_total_rmb ?? materialAmountPerUnit);
  const processRatioBase = Number(summary.process_total_rmb ?? processAmountPerUnit);
  const manufacturingRatioBase = (row) => {
    const basis = row?.manufacturing_fee_allocation_basis
      || layerA.manufacturing_fee_allocation_basis
      || DEFAULT_LAYER_A_DATA.manufacturing_fee_allocation_basis;
    if (basis === 'material_cost') return materialRatioBase;
    if (basis === 'process_cost' || basis === 'standard_hours') return processRatioBase;
    return materialRatioBase + processRatioBase;
  };
  const feeAmountPerUnit = (row, mode = manufacturingFeeMode) => {
    const normalizedMode = normalizeManufacturingFeeMode(mode, 'per_unit');
    if (normalizedMode === 'total_allocation') return quoteQty > 0 ? toNumber(row.total_amount_rmb) / quoteQty : 0;
    if (normalizedMode === 'per_unit') return toNumber(row.unit_amount_rmb ?? row.amount_rmb);
    if (normalizedMode === 'percentage') return manufacturingRatioBase(row) * toNumber(row.ratio_pct) / 100;
    return toNumber(row.amount_rmb);
  };
  const setLayerA = (patch) => onPatch({ layer_a_data: { ...layerA, ...patch } });
  const setRebateTreatment = (rebate_treatment) => setLayerA({
    rebate_treatment,
    rebate_owner: REBATE_OWNER_BY_TREATMENT[rebate_treatment] || 'none',
    factory_handles_rebate: rebate_treatment === 'factory_node'
  });
  const setMaterial = (idx, patch) => {
    const material_items = layerA.material_items.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    setLayerA({ material_items });
  };
  const addMaterial = () => setLayerA({
    material_items: [
      ...layerA.material_items,
      { name: `物料 ${layerA.material_items.length + 1}`, spec_source: '', consumption: 1, unit: '件', tax_included_unit_price_rmb: 0, input_vat_rate_pct: 13 }
    ]
  });
  const removeMaterial = (idx) => {
    if (layerA.material_items.length <= 1) return;
    setLayerA({ material_items: layerA.material_items.filter((_, i) => i !== idx) });
  };
  const setProcess = (idx, patch) => {
    const process_items = layerA.process_items.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    setLayerA({ process_items });
  };
  const addProcess = () => setLayerA({
    process_items: [
      ...layerA.process_items,
      { name: `工序 ${layerA.process_items.length + 1}`, standard_hours: 0, labor_rate_rmb_per_hour: 0, equipment_energy_rmb: 0, input_vat_rate_pct: 0 }
    ]
  });
  const removeProcess = (idx) => {
    if (layerA.process_items.length <= 1) return;
    setLayerA({ process_items: layerA.process_items.filter((_, i) => i !== idx) });
  };
  const setFee = (idx, patch) => {
    const manufacturing_fees = layerA.manufacturing_fees.map((row, i) => {
      if (i !== idx) return row;
      const next = { ...row, ...patch };
      return { ...next, amount_rmb: feeAmountPerUnit(next) };
    });
    setLayerA({ manufacturing_fees });
  };
  const addFee = () => setLayerA({
    manufacturing_fees: [
      ...layerA.manufacturing_fees,
      { label: `费用 ${layerA.manufacturing_fees.length + 1}`, allocation_rule: '按件', total_amount_rmb: 0, unit_amount_rmb: 0, ratio_pct: 0, amount_rmb: 0, input_vat_rate_pct: 0 }
    ]
  });
  const removeFee = (idx) => {
    if (layerA.manufacturing_fees.length <= 1) return;
    setLayerA({ manufacturing_fees: layerA.manufacturing_fees.filter((_, i) => i !== idx) });
  };
  const setManufacturingFeeMode = (mode) => {
    const normalizedMode = normalizeManufacturingFeeMode(mode, DEFAULT_LAYER_A_DATA.manufacturing_fee_mode);
    const manufacturing_fees = layerA.manufacturing_fees.map((row) => {
      const seeded = {
        ...row,
        total_amount_rmb: toNumber(row.total_amount_rmb) || toNumber(row.amount_rmb) * quoteQty,
        unit_amount_rmb: toNumber(row.unit_amount_rmb) || toNumber(row.amount_rmb)
      };
      return {
        ...seeded,
        manufacturing_fee_mode: normalizedMode,
        amount_rmb: feeAmountPerUnit(seeded, normalizedMode)
      };
    });
    setLayerA({ manufacturing_fee_mode: normalizedMode, manufacturing_fees });
  };
  React.useEffect(() => {
    const manufacturing_fees = layerA.manufacturing_fees.map((row) => ({
      ...row,
      amount_rmb: feeAmountPerUnit(row)
    }));
    const changed = manufacturing_fees.some((row, idx) => (
      Math.abs(toNumber(row.amount_rmb) - toNumber(layerA.manufacturing_fees[idx]?.amount_rmb)) > 0.000001
    ));
    if (changed) setLayerA({ manufacturing_fees });
  }, [
    manufacturingFeeMode,
    quoteQty,
    materialRatioBase,
    processRatioBase,
    layerA.manufacturing_fee_allocation_basis,
    layerA.manufacturing_fees
  ]);
  const downloadFactoryTemplate = () => {
    const csv = buildFactoryTemplateCsv(layerA);
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'factory-quote-template.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setFactoryMessage('模板已生成。');
  };
  const importFactoryFile = async (file) => {
    if (!file) return;
    const name = String(file.name || '').toLowerCase();
    if (name.endsWith('.xls') && !name.endsWith('.xlsx')) {
      setFactoryMessage('旧版 .xls 请另存为 .xlsx 或 CSV 后导入。');
      if (factoryFileRef.current) factoryFileRef.current.value = '';
      return;
    }
    try {
      const text = await readFactoryTemplateFile(file);
      const parsed = parseFactoryTemplateText(text);
      if (!parsed) {
        setFactoryMessage('未识别到模板内容。');
        return;
      }
      const patch = {};
      if (parsed.material_items.length > 0) patch.material_items = parsed.material_items;
      if (parsed.process_items.length > 0) patch.process_items = parsed.process_items;
      if (parsed.manufacturing_fees.length > 0) {
        const feeMode = parsed.manufacturing_fee_mode || layerA.manufacturing_fee_mode;
        patch.manufacturing_fees = parsed.manufacturing_fees.map((row) => ({
          ...row,
          amount_rmb: feeAmountPerUnit(row, feeMode)
        }));
        patch.manufacturing_fee_mode = feeMode;
      }
      if (Object.keys(patch).length === 0) {
        setFactoryMessage('未识别到物料、工序或制造费用。');
        return;
      }
      setLayerA(patch);
      setFactoryMessage(name.endsWith('.xlsx') ? 'Excel 已导入。' : 'CSV 已导入。');
      if (factoryFileRef.current) factoryFileRef.current.value = '';
    } catch (err) {
      setFactoryMessage(err?.message || '导入失败。');
      if (factoryFileRef.current) factoryFileRef.current.value = '';
    }
  };
  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-800">报价商品</h3>
            <p className="mt-1 text-xs text-slate-500">
              商品行保留原有保存和审批字段；工厂核算输出会自动承接为后续外贸基础成本。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              ref={factoryFileRef}
              type="file"
              className="hidden"
              accept=".csv,.tsv,.txt,.xls,.xlsx"
              onChange={(e) => importFactoryFile(e.target.files?.[0])}
            />
            <button type="button" className="btn-ghost text-xs" onClick={() => factoryFileRef.current?.click()}>
              导入 Excel/CSV
            </button>
            <button type="button" className="btn-ghost text-xs" onClick={downloadFactoryTemplate}>
              下载模板
            </button>
          </div>
        </div>
        {factoryMessage && <p className="mb-3 text-xs text-slate-500">{factoryMessage}</p>}
        <div className="mb-4 grid gap-2 md:grid-cols-4">
          {[
            ['1. 导入', 'Excel/CSV 或手工录入物料、工序、费用。'],
            ['2. 映射', '把客户原表字段映射到长戈核算字段。'],
            ['3. 预检', '检查数量、含税单价、进项税率和缺失项。'],
            ['4. 生成核算', '生成工厂成本、出厂价和承接口径。']
          ].map(([title, desc]) => (
            <div key={title} className="rounded-lg border border-[#e7dfcf] bg-[#fffdf8] px-3 py-2">
              <div className="text-xs font-semibold text-slate-900">{title}</div>
              <div className="mt-1 text-[11px] leading-snug text-slate-500">{desc}</div>
            </div>
          ))}
        </div>
        <StepLines form={form} onPatch={onPatch} previewLines={previewLines} />
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-800">工厂报价口径</h3>
            <p className="mt-1 text-xs text-slate-500">
              支持自有工厂、外部工厂报价和从目标利润反推。
            </p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="block">
            <span className="label">工厂业务类型</span>
            <select
              className="field"
              value={layerA.factory_business_type}
              onChange={(e) => setLayerA({ factory_business_type: e.target.value })}
            >
              <option value="direct_export">自有工厂对国外客户</option>
              <option value="domestic_trade">工厂供国内贸易公司</option>
              <option value="external_factory">外部工厂报价</option>
            </select>
          </label>
          <label className="block">
            <span className="label">销售对象</span>
            <select
              className="field"
              value={layerA.factory_sales_target}
              onChange={(e) => setLayerA({ factory_sales_target: e.target.value })}
            >
              <option value="foreign_customer">国外客户：工厂环节可判断退税</option>
              <option value="domestic_trade_company">国内贸易公司：工厂环节不退税</option>
            </select>
          </label>
          <label className="block">
            <span className="label">工厂输出口径</span>
            <select
              className="field"
              value={layerA.output_term}
              onChange={(e) => setLayerA({ output_term: e.target.value })}
            >
              <option value="EXW">EXW / 出厂价</option>
              <option value="FOB">FOB / 含出口段</option>
            </select>
          </label>
          <label className="block">
            <span className="label">报关主体</span>
            <select
              className="field"
              value={layerA.customs_subject}
              onChange={(e) => setLayerA({ customs_subject: e.target.value })}
            >
              <option value="factory">工厂</option>
              <option value="trade_company">外贸公司</option>
              <option value="customer">客户指定主体</option>
            </select>
          </label>
          <label className="block">
            <span className="label">收汇主体</span>
            <select
              className="field"
              value={layerA.collection_subject}
              onChange={(e) => setLayerA({ collection_subject: e.target.value })}
            >
              <option value="factory">工厂</option>
              <option value="trade_company">外贸公司</option>
              <option value="overseas_entity">海外运营主体</option>
            </select>
          </label>
	          <label className="block">
	            <span className="label">退税归属</span>
	            <select
	              className="field"
	              value={layerA.rebate_treatment}
	              onChange={(e) => setRebateTreatment(e.target.value)}
	            >
	              {REBATE_TREATMENT_OPTIONS.map(([value, label]) => (
	                <option key={value} value={value}>{label}</option>
	              ))}
	            </select>
	          </label>
          <label className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
	            <input
	              type="checkbox"
	              checked={layerA.rebate_treatment === 'factory_node'}
	              onChange={(e) => setRebateTreatment(e.target.checked ? 'factory_node' : 'trade_node')}
	            />
	            工厂环节处理退税
          </label>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">物料明细 / BOM</h3>
          <button type="button" className="btn-ghost text-xs" onClick={addMaterial}>+ 添加物料</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1240px] table-fixed text-sm tabular-nums">
            <colgroup>
              <col className="w-[18%]" />
              <col className="w-[18%]" />
              <col className="w-[12%]" />
              <col className="w-[10%]" />
              <col className="w-[14%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[4%]" />
            </colgroup>
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-2 py-2 text-left">物料</th>
                <th className="px-2 py-2 text-left">规格/来源</th>
                <th className="px-2 py-2 text-right">单耗</th>
                <th className="px-2 py-2 text-left">单位</th>
                <th className="px-2 py-2 text-right">含税单价</th>
                <th className="px-2 py-2 text-right">进项税率</th>
                <th className="px-2 py-2 text-right">含税金额/件</th>
                <th className="px-2 py-2 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {layerA.material_items.map((row, idx) => {
                const amountPerUnit = toNumber(row.consumption) * toNumber(row.tax_included_unit_price_rmb);
                return (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="px-2 py-2"><input className="field" value={row.name} onChange={(e) => setMaterial(idx, { name: e.target.value })} /></td>
                    <td className="px-2 py-2"><input className="field" value={row.spec_source} onChange={(e) => setMaterial(idx, { spec_source: e.target.value })} placeholder="供应商 / 规格" /></td>
                    <td className="px-2 py-2"><input type="number" min="0" step="0.0001" className="field text-right tabular-nums" value={row.consumption} onChange={(e) => setMaterial(idx, { consumption: toNumber(e.target.value) })} /></td>
                    <td className="px-2 py-2"><input className="field" value={row.unit} onChange={(e) => setMaterial(idx, { unit: e.target.value })} /></td>
                    <td className="px-2 py-2"><input type="number" min="0" step="0.01" className="field text-right tabular-nums" value={row.tax_included_unit_price_rmb} onChange={(e) => setMaterial(idx, { tax_included_unit_price_rmb: toNumber(e.target.value) })} /></td>
                    <td className="px-2 py-2"><input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums" value={row.input_vat_rate_pct} onChange={(e) => setMaterial(idx, { input_vat_rate_pct: toNumber(e.target.value) })} /></td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-slate-800 whitespace-nowrap">{formatRmb(amountPerUnit)}</td>
                    <td className="px-2 py-2 text-right"><button type="button" className="text-rose-600 disabled:text-slate-300" disabled={layerA.material_items.length <= 1} onClick={() => removeMaterial(idx)}>删除</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">工序明细 / 工艺路线</h3>
          <button type="button" className="btn-ghost text-xs" onClick={addProcess}>+ 添加工序</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1040px] table-fixed text-sm tabular-nums">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[16%]" />
              <col className="w-[16%]" />
              <col className="w-[16%]" />
              <col className="w-[14%]" />
              <col className="w-[12%]" />
              <col className="w-[4%]" />
            </colgroup>
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-2 py-2 text-left">工序</th>
                <th className="px-2 py-2 text-right">标准工时</th>
                <th className="px-2 py-2 text-right">人工费率</th>
                <th className="px-2 py-2 text-right">设备/能耗</th>
                <th className="px-2 py-2 text-right">进项税率</th>
                <th className="px-2 py-2 text-right">含税金额/件</th>
                <th className="px-2 py-2 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {layerA.process_items.map((row, idx) => {
                const amountPerUnit = toNumber(row.standard_hours) * toNumber(row.labor_rate_rmb_per_hour)
                  + toNumber(row.equipment_energy_rmb);
                return (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="px-2 py-2"><input className="field" value={row.name} onChange={(e) => setProcess(idx, { name: e.target.value })} /></td>
                    <td className="px-2 py-2"><input type="number" min="0" step="0.01" className="field text-right tabular-nums" value={row.standard_hours} onChange={(e) => setProcess(idx, { standard_hours: toNumber(e.target.value) })} /></td>
                    <td className="px-2 py-2"><input type="number" min="0" step="0.01" className="field text-right tabular-nums" value={row.labor_rate_rmb_per_hour} onChange={(e) => setProcess(idx, { labor_rate_rmb_per_hour: toNumber(e.target.value) })} /></td>
                    <td className="px-2 py-2"><input type="number" min="0" step="0.01" className="field text-right tabular-nums" value={row.equipment_energy_rmb} onChange={(e) => setProcess(idx, { equipment_energy_rmb: toNumber(e.target.value) })} /></td>
                    <td className="px-2 py-2"><input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums" value={row.input_vat_rate_pct} onChange={(e) => setProcess(idx, { input_vat_rate_pct: toNumber(e.target.value) })} /></td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-slate-800 whitespace-nowrap">{formatRmb(amountPerUnit)}</td>
                    <td className="px-2 py-2 text-right"><button type="button" className="text-rose-600 disabled:text-slate-300" disabled={layerA.process_items.length <= 1} onClick={() => removeProcess(idx)}>删除</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">制造费用与工厂规则</h3>
          <button type="button" className="btn-ghost text-xs" onClick={addFee}>+ 添加费用</button>
        </div>
        <div className="mb-4 grid gap-4 md:grid-cols-3 xl:grid-cols-5">
          <label className="block">
            <span className="label">制造费用计算方式</span>
            <select
              className="field"
              value={manufacturingFeeMode}
              onChange={(e) => setManufacturingFeeMode(e.target.value)}
            >
              {MANUFACTURING_FEE_MODE_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">分摊口径</span>
            <select
              className="field"
              value={layerA.manufacturing_fee_allocation_basis || 'quote_qty'}
              onChange={(e) => setLayerA({ manufacturing_fee_allocation_basis: e.target.value })}
            >
              {MANUFACTURING_FEE_BASIS_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">工厂毛利目标 (%)</span>
            <input type="number" min="0" max="95" step="0.01" className="field text-right tabular-nums" value={layerA.factory_margin_pct} onChange={(e) => setLayerA({ factory_margin_pct: toNumber(e.target.value) })} />
          </label>
          <label className="block">
            <span className="label">小批量上浮 (%)</span>
            <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums" value={layerA.small_batch_uplift_pct} onChange={(e) => setLayerA({ small_batch_uplift_pct: toNumber(e.target.value) })} />
          </label>
          <label className="block">
            <span className="label">资金占用天数</span>
            <input type="number" min="0" step="1" className="field text-right tabular-nums" value={layerA.capital_days} onChange={(e) => setLayerA({ capital_days: toNumber(e.target.value) })} />
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] table-fixed text-sm tabular-nums">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[20%]" />
              <col className="w-[18%]" />
              <col className="w-[16%]" />
              <col className="w-[14%]" />
              <col className="w-[10%]" />
            </colgroup>
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-2 py-2 text-left">费用项</th>
                <th className="px-2 py-2 text-left">分摊规则</th>
                <th className="px-2 py-2 text-right">
                  {manufacturingFeeMode === 'total_allocation'
                    ? '费用总额'
                    : manufacturingFeeMode === 'percentage'
                      ? '比例 (%)'
                      : manufacturingFeeMode === 'manual_override'
                        ? '覆盖金额/件'
                        : '金额/件'}
                </th>
                <th className="px-2 py-2 text-right">折算/件</th>
                <th className="px-2 py-2 text-right">进项税率</th>
                <th className="px-2 py-2 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {layerA.manufacturing_fees.map((row, idx) => {
                const amountPerUnit = feeAmountPerUnit(row);
                return (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="px-2 py-2"><input className="field" value={row.label} onChange={(e) => setFee(idx, { label: e.target.value })} placeholder="费用项" /></td>
                    <td className="px-2 py-2"><input className="field" value={row.allocation_rule} onChange={(e) => setFee(idx, { allocation_rule: e.target.value })} placeholder="分摊规则" /></td>
                    <td className="px-2 py-2">
                      {manufacturingFeeMode === 'total_allocation' && (
                        <input type="number" min="0" step="0.01" className="field text-right tabular-nums" value={row.total_amount_rmb ?? 0} onChange={(e) => setFee(idx, { total_amount_rmb: toNumber(e.target.value) })} />
                      )}
                      {manufacturingFeeMode === 'per_unit' && (
                        <input type="number" min="0" step="0.01" className="field text-right tabular-nums" value={row.unit_amount_rmb ?? row.amount_rmb ?? 0} onChange={(e) => setFee(idx, { unit_amount_rmb: toNumber(e.target.value) })} />
                      )}
                      {manufacturingFeeMode === 'percentage' && (
                        <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums" value={row.ratio_pct ?? 0} onChange={(e) => setFee(idx, { ratio_pct: toNumber(e.target.value) })} />
                      )}
                      {manufacturingFeeMode === 'manual_override' && (
                        <input type="number" min="0" step="0.01" className="field text-right tabular-nums" value={row.amount_rmb ?? 0} onChange={(e) => setFee(idx, { amount_rmb: toNumber(e.target.value) })} />
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-slate-800 whitespace-nowrap">{formatRmb(amountPerUnit)}</td>
                    <td className="px-2 py-2"><input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums" value={row.input_vat_rate_pct} onChange={(e) => setFee(idx, { input_vat_rate_pct: toNumber(e.target.value) })} /></td>
                    <td className="px-2 py-2 text-right"><button type="button" className="text-rose-600 disabled:text-slate-300" disabled={layerA.manufacturing_fees.length <= 1} onClick={() => removeFee(idx)}>删除</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <FactoryResultRow label="物料合计" value={formatRmb(summary.material_total_rmb)} />
          <FactoryResultRow label="工序合计" value={formatRmb(summary.process_total_rmb)} />
          <FactoryResultRow label="制造费用" value={formatRmb(summary.manufacturing_fee_rmb)} />
          <FactoryResultRow label="含税生产成本" value={formatRmb(summary.factory_tax_inclusive_cost_rmb)} />
          <FactoryResultRow label="小批量上浮额" value={formatRmb(summary.small_batch_uplift_rmb)} />
          <FactoryResultRow label="上浮后成本" value={formatRmb(summary.factory_cost_after_uplift_rmb)} />
          <FactoryResultRow label={`${summary.output_term || layerA.output_term} 报价`} value={formatRmb(summary.factory_quote_rmb)} />
        </div>
        <p className="mt-3 rounded-md border border-[#e7dfcf] bg-[#fffdf8] px-3 py-2 text-xs text-slate-600">
          工厂输出已自动承接到后续外贸、电商和主体测算。
        </p>
      </section>
    </div>
  );
}

function StepLines({ form, onPatch, previewLines }) {
  const setLine = (idx, patch) => {
    const next = form.line_items.slice();
    next[idx] = { ...next[idx], ...patch };
    onPatch({ line_items: next });
  };
  const removeLine = (idx) => {
    if (form.line_items.length <= 1) return;
    const next = form.line_items.slice();
    next.splice(idx, 1);
    onPatch({ line_items: next });
  };
  const addLine = () => {
    if (form.line_items.length >= 500) return;
    onPatch({ line_items: [...form.line_items, emptyLine()] });
  };

  // Task #66 — autofill from the products catalog. The pure logic lives in
  // lib/productAutofill.js so it can be unit-tested; this wrapper just
  // threads the row + product into the shared helper and applies the patch.
  // Policy: SKU is always overwritten; other fields only if current is
  // empty / 0. See computeAutofillPatch() for the exact rules.
  const autofillFromProduct = (idx, product) => {
    const row = form.line_items[idx] || emptyLine();
    const patch = computeAutofillPatch(row, product);
    setLine(idx, patch);
  };

  return (
    <div>
      <p className="mb-3 text-xs text-slate-500">
	        每行包含产品/SKU、HS 编码/退税率、数量、单件售价与成本价，以及可选的"本行追加费用"（如特殊运费）。
	          <span className="ml-1 text-slate-400">
	          产品/SKU 可选；HS 编码可按商品大类、品名或编码独立搜索。选中产品只做可选回填，仍可手动改 HS 和退税率。
	        </span>
	      </p>
	      <div className="overflow-x-auto">
	        <table className="w-full min-w-[1680px] table-fixed text-sm tabular-nums">
	          <colgroup>
	            <col className="w-[13%]" />
	            <col className="w-[15%]" />
	            <col className="w-[22%]" />
	            <col className="w-[6%]" />
	            <col className="w-[10%]" />
	            <col className="w-[10%]" />
	            <col className="w-[10%]" />
	            <col className="w-[8%]" />
	            <col className="w-[4%]" />
	            <col className="w-[2%]" />
	          </colgroup>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
	              <th className="px-2 py-2 text-left">产品/SKU</th>
	              <th className="px-2 py-2 text-left">描述</th>
	              <th className="px-2 py-2 text-left">HS 编码 / 退税率</th>
	              <th className="px-2 py-2 text-right">数量</th>
              <th className="px-2 py-2 text-right">单件售价 ¥</th>
              <th className="px-2 py-2 text-right">单件成本 ¥</th>
              <th className="px-2 py-2 text-right">本行追加费用 ¥</th>
              <th className="px-2 py-2 text-right">小计 ¥</th>
              <th className="px-2 py-2 text-right">毛利率</th>
              <th className="px-2 py-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {form.line_items.map((li, i) => {
              const preview = previewLines?.[i];
              return (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 align-top">
                    <ProductLookup
                      value={li.sku}
                      onChange={(sku) => setLine(i, { sku })}
                      onSelect={(product) => autofillFromProduct(i, product)}
                    />
                  </td>
	                  <td className="px-2 py-1.5">
	                    <input className="field" value={li.description}
	                      onChange={(e) => setLine(i, { description: e.target.value })}
	                      placeholder="" maxLength={500}/>
	                  </td>
	                  <td className="px-2 py-1.5 align-top">
	                    <HsCodeField
	                      value={li.hs_code || ''}
	                      rebateRate={li.tax_rebate_rate}
	                      onPatch={(patch) => setLine(i, patch)}
	                      enabled
	                      lineItems={[li]}
	                    />
	                  </td>
	                  <td className="px-2 py-1.5">
                    <input type="number" min="1" step="1" className="field text-right"
                      value={li.qty}
                      onChange={(e) => setLine(i, { qty: toNumber(e.target.value) })}/>
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" min="0" step="0.01" className="field text-right"
                      value={li.unit_sell_rmb}
                      onChange={(e) => setLine(i, { unit_sell_rmb: toNumber(e.target.value) })}/>
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" min="0" step="0.01" className="field text-right"
                      value={li.unit_cost_rmb}
                      onChange={(e) => setLine(i, { unit_cost_rmb: toNumber(e.target.value) })}/>
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" min="0" step="0.01" className="field text-right"
                      value={li.line_extra_cost_rmb}
                      onChange={(e) => setLine(i, { line_extra_cost_rmb: toNumber(e.target.value) })}/>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">
                    {preview?.valid ? formatRmb(preview.line_sell_rmb) : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                    {preview?.valid ? `${preview.line_margin_pct.toFixed(2)}%` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button type="button"
                      onClick={() => removeLine(i)}
                      disabled={form.line_items.length <= 1}
                      className="text-rose-600 hover:text-rose-800 disabled:text-slate-300"
                      aria-label="删除此行">×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <button type="button" className="btn-ghost" onClick={addLine}>+ 添加一行</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — B 外贸费用
// ---------------------------------------------------------------------------

const TRADE_COST_FIELDS = Object.freeze([
  ['sea_freight_rmb', '海运费'],
  ['insurance_rmb', '保险费'],
  ['customs_declaration_rmb', '报关费'],
  ['port_misc_rmb', '港杂费'],
  ['customs_clearance_rmb', '清关费'],
  ['duty_rmb', '关税'],
  ['inland_transport_rmb', '拖车/陆运费'],
  ['inspection_rmb', '商检/验货费'],
  ['payment_collection_fee_rmb', '收款费用'],
  ['financing_cost_rmb', '融资成本'],
  ['sales_commission_rmb', '佣金'],
  ['risk_reserve_rmb', '风险预提'],
  ['other_trade_cost_rmb', '其他外贸费用']
]);

function StepTradeCosts({ form, onPatch, totals }) {
  const layerB = cloneLayerBData(form.layer_b_data);
  const layerA = cloneLayerAData(form.layer_a_data);
  const currentChain = form.business_chain || layerA.business_chain || 'factory_trade_channel_entity';
  const route = quoteRouteConfig(currentChain);
  const hsPreviewItems = totals?.layer_b_data?.hs_rebate_items || [];
  const hsPreviewTotal = hsPreviewItems.reduce((acc, item) => acc + Number(item.rebate_amount_rmb || 0), 0);
  const quoteQty = totalQuoteQty(form.line_items);
  const factoryCarryTotal = Number(totals?.factory_quote_rmb || 0) * Math.max(1, quoteQty || 1);
  const currentLineCostTotal = Number(totals?.cost_subtotal_rmb || 0);
  const currentTradeQuoteTotal = Number(totals?.sell_subtotal_rmb || 0);
  const currentTradeQuotePerUnit = quoteQty > 0 ? currentTradeQuoteTotal / quoteQty : 0;
  const tradeCostSource = layerB.trade_cost_source
    || (layerB.trade_input_source === 'external_supplier' ? 'external_supplier' : null)
    || (layerB.trade_cost_basis_mode === 'manual' ? 'manual_purchase' : 'factory_output');
  const activeTradeCostSource = !route.factory && tradeCostSource === 'factory_output'
    ? 'external_supplier'
    : tradeCostSource;
  const tradeQuoteMode = layerB.trade_quote_mode || 'line_sell';
  const setLayerB = (patch) => {
    onPatch({ layer_b_data: cloneLayerBData({ ...layerB, ...patch }) });
  };
  const setTradeCostMeta = (patch) => {
    setLayerB(patch);
  };
  const setTradeCostSource = (source) => {
    if (source === 'factory_output') {
      if (!route.factory) {
        onPatch({
          layer_a_data: { ...layerA, use_factory_quote_as_cost: false },
          layer_b_data: cloneLayerBData({
            ...layerB,
            trade_action_mode: 'cost_confirmed',
            trade_cost_source: 'external_supplier',
            trade_input_source: 'external_supplier',
            trade_cost_basis_mode: 'manual'
          })
        });
        return;
      }
      onPatch({
        layer_a_data: { ...layerA, use_factory_quote_as_cost: true },
        layer_b_data: cloneLayerBData({
          ...layerB,
          trade_cost_source: 'factory_output',
          trade_input_source: 'factory_output',
          trade_cost_basis_mode: 'carry_factory',
          manual_trade_base_cost_rmb: layerB.manual_trade_base_cost_rmb || currentLineCostTotal
        })
      });
      return;
    }
    const fallbackCost = layerB.manual_trade_base_cost_rmb || currentLineCostTotal;
    onPatch({
      layer_a_data: { ...layerA, use_factory_quote_as_cost: false },
      layer_b_data: cloneLayerBData({
        ...layerB,
        trade_cost_source: source,
        trade_input_source: source,
        trade_cost_basis_mode: 'manual',
        manual_trade_base_cost_rmb: fallbackCost
      })
    });
  };
  const setManualTradeBaseCost = (value) => {
    const manualCost = toNumber(value);
    onPatch({
      layer_a_data: { ...layerA, use_factory_quote_as_cost: false },
      layer_b_data: cloneLayerBData({
        ...layerB,
        trade_cost_source: activeTradeCostSource === 'external_supplier' ? 'external_supplier' : 'manual_purchase',
        trade_input_source: activeTradeCostSource === 'external_supplier' ? 'external_supplier' : 'manual_purchase',
        trade_cost_basis_mode: 'manual',
        manual_trade_base_cost_rmb: manualCost
      }),
      line_items: distributeUnitCostByQty(form.line_items, manualCost)
    });
  };
  const setTradeQuoteMode = (mode) => {
    setLayerB({
      trade_quote_mode: mode,
      manual_trade_quote_rmb: mode === 'manual' ? (layerB.manual_trade_quote_rmb || currentTradeQuoteTotal) : layerB.manual_trade_quote_rmb
    });
  };
  React.useEffect(() => {
    if (route.factory) return;
    const carriesFactoryCost = layerB.trade_cost_source === 'factory_output'
      || layerB.trade_input_source === 'factory_output'
      || layerB.trade_cost_basis_mode === 'carry_factory'
      || layerA.use_factory_quote_as_cost;
    if (!carriesFactoryCost) return;
    onPatch({
      layer_a_data: { ...layerA, use_factory_quote_as_cost: false },
      layer_b_data: cloneLayerBData({
        ...layerB,
        trade_action_mode: 'cost_confirmed',
        trade_cost_source: 'external_supplier',
        trade_input_source: 'external_supplier',
        trade_cost_basis_mode: 'manual'
      })
    });
  }, [
    route.factory,
    layerA.use_factory_quote_as_cost,
    layerB.trade_cost_source,
    layerB.trade_input_source,
    layerB.trade_cost_basis_mode,
    onPatch
  ]);
  const setManualTradeQuote = (value) => {
    const quoteTotal = toNumber(value);
    onPatch({
      layer_b_data: cloneLayerBData({
        ...layerB,
        trade_quote_mode: 'manual',
        manual_trade_quote_rmb: quoteTotal
      }),
      line_items: distributeUnitSellByQty(form.line_items, quoteTotal)
    });
  };
  return (
    <div className="space-y-6">
        <div className="rounded-lg border border-[#e7dfcf] bg-[#fffdf8] p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-medium text-slate-700">外贸报价确认</h3>
              <p className="mt-1 text-xs text-slate-500">
                B 节点需要确认对客户/外部买方的报价，报价会回写商品行售价并进入外贸利润率。
              </p>
            </div>
            <span className="rounded-full border border-[#e7dfcf] bg-white px-3 py-1 text-xs font-medium text-slate-600">
              当前外贸报价 {formatRmb(currentTradeQuoteTotal)} · {formatRmb(currentTradeQuotePerUnit)}/件
            </span>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <button
              type="button"
              aria-pressed={tradeQuoteMode === 'line_sell'}
              className={[
                'rounded-lg border px-3 py-3 text-left transition',
                tradeQuoteMode === 'line_sell'
                  ? 'border-[#c9b58c] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d8c69d]'
                  : 'border-[#e7dfcf] bg-white text-slate-700 hover:bg-[#fffaf0]'
              ].join(' ')}
              onClick={() => setTradeQuoteMode('line_sell')}
            >
              <span className="block text-sm font-semibold">使用商品行客户报价</span>
              <span className="mt-1 block text-xs text-slate-500">
                商品行售价就是 B 外贸对外报价，适合已有客户明细。
              </span>
            </button>
            <button
              type="button"
              aria-pressed={tradeQuoteMode === 'manual'}
              className={[
                'rounded-lg border px-3 py-3 text-left transition',
                tradeQuoteMode === 'manual'
                  ? 'border-[#c9b58c] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d8c69d]'
                  : 'border-[#e7dfcf] bg-white text-slate-700 hover:bg-[#fffaf0]'
              ].join(' ')}
              onClick={() => setTradeQuoteMode('manual')}
            >
              <span className="block text-sm font-semibold">手动确认外贸报价</span>
              <span className="mt-1 block text-xs text-slate-500">
                适合纯外贸报价到外部买方，先锁定 B 报价再看外贸段利润。
              </span>
            </button>
          </div>
          {tradeQuoteMode === 'manual' && (
            <label className="mt-3 block">
              <span className="label">外贸报价合计 (¥)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                className="field text-right tabular-nums"
                value={layerB.manual_trade_quote_rmb || currentTradeQuoteTotal}
                onChange={(e) => setManualTradeQuote(e.target.value)}
              />
              <span className="hint">输入后按报价数量折算到商品行单件售价，驾驶舱外贸报价同步更新。</span>
            </label>
          )}
        </div>
        <div className="rounded-lg border border-[#e7dfcf] bg-[#fffdf8] p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-medium text-slate-700">外贸成本确认</h3>
              <p className="mt-1 text-xs text-slate-500">
                确认 B 节点使用的基础成本，可承接 A 工厂报价，也可手动录入外贸采购/到港前基础成本。
              </p>
            </div>
            <span className="rounded-full border border-[#e7dfcf] bg-white px-3 py-1 text-xs font-medium text-slate-600">
              当前基础成本 {formatRmb(currentLineCostTotal)}
            </span>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {route.factory && (
              <button
                type="button"
                aria-pressed={activeTradeCostSource === 'factory_output'}
                className={[
                  'rounded-lg border px-3 py-3 text-left transition',
                  activeTradeCostSource === 'factory_output'
                    ? 'border-[#c9b58c] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d8c69d]'
                    : 'border-[#e7dfcf] bg-white text-slate-700 hover:bg-[#fffaf0]'
                ].join(' ')}
                onClick={() => setTradeCostSource('factory_output')}
              >
                <span className="block text-sm font-semibold">承接工厂报价</span>
                <span className="mt-1 block text-xs text-slate-500">
                  使用 A 节点工厂报价作为 B 外贸基础成本：{formatRmb(factoryCarryTotal)}
                </span>
              </button>
            )}
            <button
              type="button"
              aria-pressed={activeTradeCostSource === 'manual_purchase'}
              className={[
                'rounded-lg border px-3 py-3 text-left transition',
                activeTradeCostSource === 'manual_purchase'
                  ? 'border-[#c9b58c] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d8c69d]'
                  : 'border-[#e7dfcf] bg-white text-slate-700 hover:bg-[#fffaf0]'
              ].join(' ')}
              onClick={() => setTradeCostSource('manual_purchase')}
            >
              <span className="block text-sm font-semibold">手动确认基础成本</span>
              <span className="mt-1 block text-xs text-slate-500">
                适合 B 外贸节点单独核价或采购价已由业务确认。
              </span>
            </button>
            <button
              type="button"
              aria-pressed={activeTradeCostSource === 'external_supplier'}
              className={[
                'rounded-lg border px-3 py-3 text-left transition',
                activeTradeCostSource === 'external_supplier'
                  ? 'border-[#c9b58c] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d8c69d]'
                  : 'border-[#e7dfcf] bg-white text-slate-700 hover:bg-[#fffaf0]'
              ].join(' ')}
              onClick={() => setTradeCostSource('external_supplier')}
            >
              <span className="block text-sm font-semibold">外部供应商报价</span>
              <span className="mt-1 block text-xs text-slate-500">
                外采工厂或供应商报价作为 B 外贸基础采购成本。
              </span>
            </button>
          </div>
          {activeTradeCostSource !== 'factory_output' && (
            <label className="mt-3 block">
              <span className="label">
                {activeTradeCostSource === 'external_supplier' ? '外部供应商报价合计 (¥)' : '外贸基础采购成本合计 (¥)'}
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                className="field text-right tabular-nums"
                value={layerB.manual_trade_base_cost_rmb ?? currentLineCostTotal}
                onChange={(e) => setManualTradeBaseCost(e.target.value)}
              />
              <span className="hint">输入后会按报价数量折算到商品行成本，作为后续外贸利润计算依据。</span>
            </label>
          )}
        </div>
        <div>
          <h3 className="mb-2 font-medium text-slate-700">外贸成本链</h3>
          <p className="mb-3 text-xs text-slate-500">
            B 模块只处理外贸段：海运、保险、报关、港杂、清关、关税、收款、融资、佣金和风险预提进入 FOB / CIF / DDP 口径。
          </p>
          <div className="space-y-3">
            {TRADE_COST_FIELDS.map(([key, label]) => {
              const subtotal = toNumber(layerB[key]);
              const calculatedAmount = Number(totals?.layer_b_data?.breakdown?.[key]);
              const effectiveAmount = Number.isFinite(calculatedAmount) ? calculatedAmount : subtotal;
              const unitAmount = quoteQty > 0 ? effectiveAmount / quoteQty : 0;
              const ratioKey = `${key}_ratio_pct`;
              const ruleKey = `${key}_rule`;
              return (
                <div key={key} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="mb-2 text-sm font-semibold text-slate-800">{label}</div>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <label className="block">
                      <span className="label">项目小计 (¥)</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="field text-right tabular-nums"
                        value={layerB[key] ?? 0}
                        onChange={(e) => setLayerB({ [key]: toNumber(e.target.value) })}
                      />
                    </label>
                    <label className="block">
                      <span className="label">比例 (%)</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className="field text-right tabular-nums"
                        value={layerB[ratioKey] ?? 0}
                        onChange={(e) => setTradeCostMeta({ [ratioKey]: toNumber(e.target.value) })}
                      />
                    </label>
                    <label className="block">
                      <span className="label">规则</span>
                      <select
                        className="field"
                        value={layerB[ruleKey] || 'fixed_order'}
                        onChange={(e) => setTradeCostMeta({ [ruleKey]: e.target.value })}
                      >
                        {TRADE_COST_RULE_OPTIONS.map(([value, ruleLabel]) => (
                          <option key={value} value={value}>{ruleLabel}</option>
                        ))}
                      </select>
                    </label>
                    <div className="rounded-md border border-[#e7dfcf] bg-[#fffdf8] px-3 py-2">
                      <div className="text-[11px] text-slate-500">自动折算/件</div>
                      <div className="mt-1 text-right text-sm font-semibold tabular-nums text-slate-900">{formatRmb(unitAmount)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

	        <div className="rounded-md border border-slate-200 p-3">
	          <h3 className="mb-3 font-medium text-slate-700">外贸海关税务汇总</h3>
	          <dl className="grid gap-2 text-sm sm:grid-cols-2">
	            <div>
	              <dt className="text-slate-500">商品行 HS 项</dt>
	              <dd className="font-medium text-slate-800">
	                {hsPreviewItems.length > 0 ? `${hsPreviewItems.length} 项` : '未填写'}
	              </dd>
	            </div>
	            <div>
	              <dt className="text-slate-500">退税归属</dt>
	              <dd className="font-medium text-slate-800">
	                {REBATE_TREATMENT_OPTIONS.find(([value]) => value === cloneLayerAData(form.layer_a_data).rebate_treatment)?.[1] || '未设置'}
	              </dd>
	            </div>
	            <div>
	              <dt className="text-slate-500">外贸费用合计</dt>
	              <dd className="font-medium text-slate-800">{formatRmb(totals?.trade_cost_rmb)}</dd>
	            </div>
	            <div>
	              <dt className="text-slate-500">行级退税合计</dt>
	              <dd className={[
	                'font-medium',
	                Number(hsPreviewTotal || 0) < 0 ? 'text-amber-700' : 'text-emerald-700'
	              ].join(' ')}>
	                {formatRmb(hsPreviewTotal)}
	              </dd>
	            </div>
	          </dl>
	          {hsPreviewItems.length === 0 ? (
	            <p className="mt-4 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
	              请在商品行的产品/描述后维护 HS 编码和退税率；外贸页只汇总，不再作为唯一输入点。
	            </p>
	          ) : (
	            <div className="mt-4 overflow-x-auto">
	              <table className="w-full min-w-[720px] table-fixed text-sm tabular-nums">
	                <colgroup>
	                  <col className="w-[18%]" />
	                  <col className="w-[30%]" />
	                  <col className="w-[18%]" />
	                  <col className="w-[14%]" />
	                  <col className="w-[20%]" />
	                </colgroup>
	                <thead className="bg-slate-50 text-xs text-slate-500">
	                  <tr>
	                    <th className="px-2 py-2 text-left">HS 编码</th>
	                    <th className="px-2 py-2 text-left">商品行</th>
	                    <th className="px-2 py-2 text-right">退税基数</th>
	                    <th className="px-2 py-2 text-right">退税率</th>
	                    <th className="px-2 py-2 text-right">退税额</th>
	                  </tr>
	                </thead>
	                <tbody>
	                  {hsPreviewItems.map((item, idx) => (
	                    <tr key={`${item.hs_code || 'blank'}-${idx}`} className="border-t border-slate-100">
	                      <td className="px-2 py-2">{item.hs_code || '未填写'}</td>
	                      <td className="px-2 py-2 text-slate-700">{item.label || `商品行 ${idx + 1}`}</td>
	                      <td className="px-2 py-2 text-right">{formatRmb(item.base_amount_rmb)}</td>
	                      <td className="px-2 py-2 text-right">{roundPercent(Number(item.tax_rebate_rate || 0) * 100)}%</td>
	                      <td className={[
	                        'px-2 py-2 text-right font-semibold',
	                        Number(item.rebate_amount_rmb || 0) < 0 ? 'text-amber-700' : 'text-emerald-700'
	                      ].join(' ')}>
	                        {formatRmb(item.rebate_amount_rmb)}
	                      </td>
	                    </tr>
	                  ))}
	                </tbody>
	              </table>
	            </div>
	          )}
	        </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — C 电商渠道成本
// ---------------------------------------------------------------------------

function StepChannelCosts({ form, onPatch, totals }) {
  const layerC = { ...DEFAULT_LAYER_C_DATA, ...(form.layer_c_data || {}) };
  const quoteQty = Math.max(1, totalQuoteQty(form.line_items) || 1);
  const tradeDdpBasis = Number(totals?.cost_subtotal_rmb || 0)
    + Number(totals?.line_extra_cost_rmb || 0)
    + Number(totals?.shared_cost_rmb || 0)
    + Number(totals?.trade_cost_rmb || 0)
    + Math.max(0, -Number(totals?.export_rebate_rmb || 0));
  const canonicalChannelBasisMode = layerC.cost_basis_mode
    || (layerC.channel_cost_basis_mode === 'manual' ? 'manual' : 'inherit_trade');
  const channelBasisMode = canonicalChannelBasisMode === 'manual' ? 'manual' : 'carry_trade';
  const manualChannelBasisPerUnit = toNumber(
    layerC.manual_basis_cost_per_unit_rmb ?? layerC.manual_channel_base_cost_rmb
  );
  const channelBasisTotal = canonicalChannelBasisMode === 'manual'
    ? manualChannelBasisPerUnit * quoteQty
    : tradeDdpBasis;
  const setLayerC = (patch) => {
    onPatch({ layer_c_data: { ...layerC, ...patch } });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
      <div className="rounded-lg border border-[#e7dfcf] bg-[#fffdf8] p-4">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-medium text-slate-700">电商成本确认</h3>
            <p className="mt-1 text-xs text-slate-500">
              确认 C 节点平台测算的基础成本，可承接外贸 DDP/落地口径，也可手动录入平台基础成本。
            </p>
          </div>
          <span className="rounded-full border border-[#e7dfcf] bg-white px-3 py-1 text-xs font-medium text-slate-600">
            {formatRmb(channelBasisTotal)} · {formatRmb(channelBasisTotal / quoteQty)}/件
          </span>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <button
            type="button"
            aria-pressed={channelBasisMode === 'carry_trade'}
            className={[
              'rounded-lg border px-3 py-3 text-left transition',
              channelBasisMode === 'carry_trade'
                ? 'border-[#c9b58c] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d8c69d]'
                : 'border-[#e7dfcf] bg-white text-slate-700 hover:bg-[#fffaf0]'
            ].join(' ')}
            onClick={() => setLayerC({
              cost_basis_mode: 'inherit_trade',
              channel_cost_basis_mode: 'carry_trade'
            })}
          >
            <span className="block text-sm font-semibold">承接外贸报价 / DDP</span>
            <span className="mt-1 block text-xs text-slate-500">
              使用 B 节点成本和外贸费用形成平台基础成本：{formatRmb(tradeDdpBasis)}
            </span>
          </button>
          <button
            type="button"
            aria-pressed={channelBasisMode === 'manual'}
            className={[
              'rounded-lg border px-3 py-3 text-left transition',
              channelBasisMode === 'manual'
                ? 'border-[#c9b58c] bg-[#fff7e6] text-slate-950 shadow-sm ring-1 ring-[#d8c69d]'
                : 'border-[#e7dfcf] bg-white text-slate-700 hover:bg-[#fffaf0]'
            ].join(' ')}
            onClick={() => {
              const perUnit = manualChannelBasisPerUnit || (tradeDdpBasis / quoteQty);
              setLayerC({
                cost_basis_mode: 'manual',
                channel_cost_basis_mode: 'manual',
                manual_basis_cost_per_unit_rmb: perUnit,
                manual_channel_base_cost_rmb: perUnit
              });
            }}
          >
            <span className="block text-sm font-semibold">手动确认平台基础成本</span>
            <span className="mt-1 block text-xs text-slate-500">
              适合平台已有采购/入仓成本，单独作为 C 节点利润率口径。
            </span>
          </button>
        </div>
        {channelBasisMode === 'manual' && (
          <label className="mt-3 block">
            <span className="label">平台基础成本 (¥/件)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="field text-right tabular-nums"
              value={manualChannelBasisPerUnit}
              onChange={(e) => {
                const perUnit = toNumber(e.target.value);
                setLayerC({
                  cost_basis_mode: 'manual',
                  channel_cost_basis_mode: 'manual',
                  manual_basis_cost_per_unit_rmb: perUnit,
                  manual_channel_base_cost_rmb: perUnit
                });
              }}
            />
            <span className="hint">系统按报价数量折算为 C 节点成本基础合计，用于电商利润率和主体利润计算。</span>
          </label>
        )}
      </div>

      <div className="rounded-md border border-slate-200 p-4">
        <h3 className="mb-2 font-medium text-slate-700">跨境 / 电商渠道扣点</h3>
        <p className="mb-4 text-xs text-slate-500">
          C 模块承接 B 的 DDP / 已到仓利润口径，只处理平台佣金、广告、FBA、仓储、退货、售后、税费、结算和库存资金占用。
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label">平台佣金 (%)</label>
            <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums"
	              value={layerC.platform_commission_pct ?? 0}
              onChange={(e) => setLayerC({ platform_commission_pct: toNumber(e.target.value) })}/>
          </div>
          <div>
            <label className="label">支付手续费 (%)</label>
            <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums"
	              value={layerC.payment_fee_pct ?? 0}
              onChange={(e) => setLayerC({ payment_fee_pct: toNumber(e.target.value) })}/>
          </div>
          <div>
            <label className="label">广告 ACOS (%)</label>
            <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums"
	              value={layerC.ad_rate_pct ?? 0}
              onChange={(e) => setLayerC({ ad_rate_pct: toNumber(e.target.value) })}/>
          </div>
          <div>
            <label className="label">退货损耗率 (%)</label>
            <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums"
	              value={layerC.return_rate_pct ?? 0}
              onChange={(e) => setLayerC({ return_rate_pct: toNumber(e.target.value) })}/>
          </div>
          <div>
            <label className="label">FBA / 配送费 (¥/件)</label>
            <input type="number" min="0" step="0.01" className="field text-right tabular-nums"
	              value={layerC.fba_fee_per_unit_rmb ?? 0}
              onChange={(e) => setLayerC({ fba_fee_per_unit_rmb: toNumber(e.target.value) })}/>
          </div>
          <div>
            <label className="label">海外仓储费 (¥)</label>
            <input type="number" min="0" step="0.01" className="field text-right tabular-nums"
	              value={layerC.overseas_storage_rmb ?? 0}
              onChange={(e) => setLayerC({ overseas_storage_rmb: toNumber(e.target.value) })}/>
          </div>
          <div>
            <label className="label">售后成本 (¥)</label>
            <input type="number" min="0" step="0.01" className="field text-right tabular-nums"
	              value={layerC.after_sales_cost_rmb ?? 0}
              onChange={(e) => setLayerC({ after_sales_cost_rmb: toNumber(e.target.value) })}/>
          </div>
          <div>
            <label className="label">渠道税费 (¥)</label>
            <input type="number" min="0" step="0.01" className="field text-right tabular-nums"
	              value={layerC.channel_tax_rmb ?? 0}
              onChange={(e) => setLayerC({ channel_tax_rmb: toNumber(e.target.value) })}/>
          </div>
          <div>
            <label className="label">结算费用 (¥)</label>
            <input type="number" min="0" step="0.01" className="field text-right tabular-nums"
	              value={layerC.settlement_fee_rmb ?? 0}
              onChange={(e) => setLayerC({ settlement_fee_rmb: toNumber(e.target.value) })}/>
          </div>
          <div>
            <label className="label">库存资金占用 (¥)</label>
            <input type="number" min="0" step="0.01" className="field text-right tabular-nums"
	              value={layerC.inventory_capital_cost_rmb ?? 0}
              onChange={(e) => setLayerC({ inventory_capital_cost_rmb: toNumber(e.target.value) })}/>
          </div>
          <div className="sm:col-span-2">
            <label className="label">其他渠道成本 (¥)</label>
            <input type="number" min="0" step="0.01" className="field text-right tabular-nums"
	              value={layerC.other_channel_cost_rmb ?? 0}
              onChange={(e) => setLayerC({ other_channel_cost_rmb: toNumber(e.target.value) })}/>
          </div>
        </div>
      </div>
      </div>

      <div className="rounded-md border border-slate-200 p-4">
        <h3 className="mb-3 font-medium text-slate-700">渠道扣点结果</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-500">渠道成本</dt>
            <dd className="font-medium text-slate-800">{formatRmb(totals?.channel_cost_rmb)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">进入主体前利润</dt>
            <dd className="font-medium text-slate-800">{formatRmb(totals?.profit_before_entity_rmb)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — E 主体利润 / 税
// ---------------------------------------------------------------------------

function StepEntityCosts({ form, onPatch, totals }) {
  const current = layerInputs(form.layer_e_data, DEFAULT_LAYER_E_DATA);
  const entities = Array.isArray(current.overseas_entities) && current.overseas_entities.length > 0
    ? current.overseas_entities
    : DEFAULT_LAYER_E_DATA.overseas_entities.map((entity) => ({ ...entity }));
  const expenseItems = Array.isArray(current.entity_expense_items) && current.entity_expense_items.length > 0
    ? current.entity_expense_items
    : defaultEntityExpenseItems();
  const expenseAllocatedInputTotal = expenseItems.reduce((acc, item) => acc + toNumber(item.allocated_amount_rmb), 0);
  const expenseAllocatedTotal = Number.isFinite(Number(totals?.layer_e_data?.basis?.entity_expense_total_rmb))
    ? Number(totals.layer_e_data.basis.entity_expense_total_rmb)
    : expenseAllocatedInputTotal;
  const patchLayerE = (patch) => {
    const nextExpenseItems = (patch.entity_expense_items || expenseItems).map((item, idx) => ({
      ...item,
      label: item?.label || defaultEntityExpenseItems()[idx]?.label || `主体费用 ${idx + 1}`,
      allocation_method: normalizeEntityExpenseMethod(item?.allocation_method, defaultEntityExpenseItems()[idx]?.allocation_method || 'sales_share')
    }));
    const nextEntities = (patch.overseas_entities || entities).map((entity) => ({ ...entity }));
    const next = {
      ...current,
      ...patch,
      entity_expense_items: nextExpenseItems,
      overseas_entities: nextEntities
    };
    if (Object.prototype.hasOwnProperty.call(patch, 'overseas_profit_share_pct')) {
      next.transfer_pricing_pct = patch.overseas_profit_share_pct;
    }
    onPatch({ layer_e_data: next });
  };
  const deriveExpenseItem = (item) => {
    const allocation_method = normalizeEntityExpenseMethod(item.allocation_method, 'sales_share');
    if (allocation_method === 'manual') return { ...item, allocation_method };
    return {
      ...item,
      allocation_method,
      allocated_amount_rmb: toNumber(item.total_amount_rmb) * toNumber(item.allocation_pct) / 100
    };
  };
  const patchExpenseItem = (idx, patch) => {
    const nextItems = expenseItems.map((item, i) => {
      if (i !== idx) return { ...item };
      return deriveExpenseItem({ ...item, ...patch });
    });
    patchLayerE({ entity_expense_items: nextItems });
  };
  const addExpenseItem = () => {
    patchLayerE({
      entity_expense_items: [
        ...expenseItems.map((item) => ({ ...item })),
        { label: `主体费用 ${expenseItems.length + 1}`, entity_name: entities[0]?.name || 'E1 海外运营主体', total_amount_rmb: 0, allocation_method: 'manual', allocation_pct: 0, allocated_amount_rmb: 0 }
      ]
    });
  };
  const removeExpenseItem = (idx) => {
    if (expenseItems.length <= 5) return;
    const nextItems = expenseItems.filter((_, i) => i !== idx).map((item) => ({ ...item }));
    patchLayerE({ entity_expense_items: nextItems });
  };
  const patchEntity = (idx, patch) => {
    const nextEntities = entities.map((entity, i) => i === idx ? { ...entity, ...patch } : { ...entity });
    patchLayerE({ overseas_entities: nextEntities });
  };
  const addEntity = () => {
    if (entities.length >= 20) return;
    patchLayerE({
      overseas_entities: [
        ...entities.map((entity) => ({ ...entity })),
        { name: `海外运营主体 ${entities.length + 1}`, profit_share_pct: 0, operating_expense_rmb: 0, income_tax_pct: 0, flow_tax_pct: 0 }
      ]
    });
  };
  const removeEntity = (idx) => {
    if (entities.length <= 1) return;
    patchLayerE({ overseas_entities: entities.filter((_, i) => i !== idx).map((entity) => ({ ...entity })) });
  };
  const shareTotal = entities.reduce((acc, entity) => acc + toNumber(entity.profit_share_pct), 0);
  const domesticShare = Math.max(0, 100 - toNumber(current.overseas_profit_share_pct));
  const entitySplit = totals?.layer_e_data?.split;

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-slate-200 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-medium text-slate-700">运营主体费用分摊</h3>
            <p className="text-xs text-slate-500">本单先承担主体费用，再进入国内、海外主体利润分配。</p>
          </div>
          <button type="button" className="btn-ghost" onClick={addExpenseItem}>+ 添加费用</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1180px] table-fixed text-sm tabular-nums">
            <colgroup>
              <col className="w-[18%]" />
              <col className="w-[18%]" />
              <col className="w-[16%]" />
              <col className="w-[18%]" />
              <col className="w-[12%]" />
              <col className="w-[14%]" />
              <col className="w-[4%]" />
            </colgroup>
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-2 py-2 text-left">费用项</th>
                <th className="px-2 py-2 text-left">主体</th>
                <th className="px-2 py-2 text-right">主体总费用</th>
                <th className="px-2 py-2 text-left">分摊方式</th>
                <th className="px-2 py-2 text-right">分摊占比</th>
                <th className="px-2 py-2 text-right">本单分摊</th>
                <th className="px-2 py-2 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {expenseItems.map((item, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-2 py-2">
                    <input className="field" value={item.label || ''} onChange={(e) => patchExpenseItem(idx, { label: e.target.value })} />
                  </td>
                  <td className="px-2 py-2">
                    <input className="field" value={item.entity_name || ''} onChange={(e) => patchExpenseItem(idx, { entity_name: e.target.value })} placeholder="E1 日本运营主体" />
                  </td>
                  <td className="px-2 py-2">
                    <input type="number" min="0" step="0.01" className="field text-right tabular-nums" value={item.total_amount_rmb ?? 0} onChange={(e) => patchExpenseItem(idx, { total_amount_rmb: toNumber(e.target.value) })} />
                  </td>
                  <td className="px-2 py-2">
                    <select className="field" value={normalizeEntityExpenseMethod(item.allocation_method, 'sales_share')} onChange={(e) => patchExpenseItem(idx, { allocation_method: e.target.value })}>
                      {ENTITY_EXPENSE_METHOD_OPTIONS.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums" value={item.allocation_pct ?? 0} onChange={(e) => patchExpenseItem(idx, { allocation_pct: toNumber(e.target.value) })} />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="field text-right tabular-nums"
                      value={item.allocated_amount_rmb ?? 0}
                      onChange={(e) => patchExpenseItem(idx, { allocation_method: 'manual', allocated_amount_rmb: toNumber(e.target.value) })}
                    />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button type="button" className="text-rose-600 disabled:text-slate-300" disabled={expenseItems.length <= 5} onClick={() => removeExpenseItem(idx)}>删除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <FactoryResultRow label="本单运营主体费用" value={formatRmb(expenseAllocatedTotal)} />
          <FactoryResultRow label="费用项数量" value={`${expenseItems.length} 项`} />
          <FactoryResultRow label="进入分配前扣减" value={formatRmb(expenseAllocatedTotal)} />
        </div>
      </div>

      <div className="rounded-md border border-slate-200 p-4">
        <h3 className="mb-2 font-medium text-slate-700">主体利润分配</h3>
        <p className="mb-4 text-xs text-slate-500">
          E 模块承接 C 扣点后的利润；若未开通 C，则承接 B 外贸利润。费用扣减后，再决定利润留在国内还是进入海外运营主体。
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="label">海外运营主体利润占比 (%)</label>
            <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums"
              value={current.overseas_profit_share_pct ?? 0}
              onChange={(e) => patchLayerE({ overseas_profit_share_pct: toNumber(e.target.value) })}/>
            <p className="hint">国内利润占比：{domesticShare.toFixed(2)}%</p>
          </div>
          <div>
            <label className="label">中国所得税率 (%)</label>
            <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums"
              value={current.china_income_tax_pct ?? 25}
              onChange={(e) => patchLayerE({ china_income_tax_pct: toNumber(e.target.value) })}/>
          </div>
          <div className="md:col-span-2">
            <label className="label">回款路径</label>
            <input
              className="field"
              value={current.settlement_path || '平台 → 海外主体 → 国内分红/服务费'}
              onChange={(e) => patchLayerE({ settlement_path: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-medium text-slate-700">海外运营主体</h3>
            <p className="text-xs text-slate-500">至少保留一个海外运营主体；多个主体时，分配占比按合计比例执行。</p>
          </div>
          <button type="button" className="btn-ghost" onClick={addEntity}>+ 添加主体</button>
        </div>
        <div className="space-y-3">
          {entities.map((entity, idx) => (
            <div key={idx} className="grid gap-3 rounded-md border border-slate-100 p-3 md:grid-cols-[1.4fr_0.85fr_0.85fr_0.85fr_auto]">
              <div>
                <label className="label">主体名称</label>
                <input className="field" value={entity.name || ''}
                  onChange={(e) => patchEntity(idx, { name: e.target.value })}/>
              </div>
              <div>
                <label className="label">分配占比 (%)</label>
                <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums"
                  value={entity.profit_share_pct ?? 0}
                  onChange={(e) => patchEntity(idx, { profit_share_pct: toNumber(e.target.value) })}/>
              </div>
              <div>
                <label className="label">所得税率 (%)</label>
                <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums"
                  value={entity.income_tax_pct ?? 0}
                  onChange={(e) => patchEntity(idx, { income_tax_pct: toNumber(e.target.value) })}/>
              </div>
              <div>
                <label className="label">流转税 (%)</label>
                <input type="number" min="0" max="100" step="0.01" className="field text-right tabular-nums"
                  value={entity.flow_tax_pct ?? 0}
                  onChange={(e) => patchEntity(idx, { flow_tax_pct: toNumber(e.target.value) })}/>
              </div>
              <div className="flex items-end">
                <button type="button" className="text-rose-600 hover:text-rose-800 disabled:text-slate-300"
                  disabled={entities.length <= 1}
                  onClick={() => removeEntity(idx)}>
                  删除
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className={`mt-2 text-xs ${Math.abs(shareTotal - 100) > 0.01 ? 'text-amber-700' : 'text-slate-500'}`}>
          海外运营主体分配占比合计：{shareTotal.toFixed(2)}%。系统会按该合计比例折算；建议填到 100%。
        </p>
      </div>

      <div className="rounded-md border border-slate-200 p-4">
        <h3 className="mb-3 font-medium text-slate-700">主体税费结果</h3>
        <dl className="grid gap-2 text-sm md:grid-cols-3">
          <div>
            <dt className="text-slate-500">费用后利润池</dt>
            <dd className="font-medium text-slate-800">
              {formatRmb(totals?.layer_e_data?.basis?.distributable_profit_pool_rmb ?? totals?.profit_before_entity_rmb)}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">中国所得税</dt>
            <dd className="font-medium text-slate-800">{formatRmb(entitySplit?.china_income_tax_rmb)}</dd>
          </div>
          <div>
            <dt className="text-slate-500">海外所得税</dt>
            <dd className="font-medium text-slate-800">{formatRmb(entitySplit?.overseas_income_tax_rmb)}</dd>
          </div>
          <div>
            <dt className="text-slate-500">海外流转税</dt>
            <dd className="font-medium text-slate-800">{formatRmb(entitySplit?.overseas_flow_tax_rmb)}</dd>
          </div>
          <div>
            <dt className="text-slate-500">国内税后利润</dt>
            <dd className="font-medium text-slate-800">
              {formatRmb(Number(entitySplit?.domestic_gross_profit_rmb || 0)
                - Number(entitySplit?.china_income_tax_rmb || 0))}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">海外税后利润</dt>
            <dd className="font-medium text-slate-800">
              {formatRmb(Number(entitySplit?.overseas_gross_profit_rmb || 0)
                - Number(entitySplit?.overseas_flow_tax_rmb || 0)
                - Number(entitySplit?.overseas_income_tax_rmb || 0))}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">集团净利润</dt>
            <dd className="font-medium text-slate-800">{formatRmb(totals?.group_net_profit_rmb)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

function DecisionMetric({ label, value, note }) {
  return (
    <div className="min-w-0 rounded-lg border border-[#e7dfcf] bg-[#fffdf8] px-3 py-2">
      <div className="text-[11px] font-medium text-slate-500">{label}</div>
      <div className="mt-0.5 break-words text-[13px] font-semibold leading-snug tabular-nums text-slate-950 [overflow-wrap:anywhere]">{value}</div>
      {note && <div className="mt-0.5 text-[11px] text-slate-500">{note}</div>}
    </div>
  );
}

function DecisionStageRow({ label, items }) {
  return (
    <div className="rounded-lg border border-[#e7dfcf] bg-[#fffdf8] p-2">
      <div className="grid grid-cols-[3rem_minmax(0,1fr)] gap-2">
        <div className="flex items-center justify-center rounded-md bg-[#f5efe3] px-1 text-center text-sm font-semibold text-slate-900">
          {label}
        </div>
        <div className="grid gap-2">
          {items.map((item) => (
            <div key={item.label} className="grid grid-cols-[minmax(4.25rem,0.9fr)_minmax(0,1.35fr)] items-start gap-2">
              <span className="min-w-0 text-[11px] leading-snug text-slate-500">{item.label}</span>
              <span className="min-w-0 break-words text-right text-[13px] font-semibold leading-snug tabular-nums text-slate-950 [overflow-wrap:anywhere]">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatCompactNumber(value, suffix = '') {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return `—${suffix}`;
  return `${n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}${suffix}`;
}

function CockpitNumber({ label, value, note }) {
  return (
    <div className="min-w-0 rounded-lg border border-[#e7dfcf] bg-white px-3 py-2">
      <div className="text-[11px] font-medium text-slate-500">{label}</div>
      <div className="mt-1 break-words text-[clamp(0.86rem,1.1vw,1rem)] font-bold leading-tight tabular-nums text-slate-950 [overflow-wrap:anywhere]">
        {value}
      </div>
      {note && <div className="mt-1 text-[11px] leading-snug text-slate-500">{note}</div>}
    </div>
  );
}

function QuoteDecisionPanel({
  form,
  totals,
  onPatch,
  saving,
  canSubmit,
  canSave = true,
  validationHint = null,
  previewErrors = [],
  onSaveDraft,
  onPreview,
  onSubmit,
  collapsed = false,
  onToggleCollapsed
}) {
  const layerA = cloneLayerAData(form.layer_a_data);
  const qty = totalQuoteQty(form.line_items);
  const setQuoteLogic = (quote_logic) => {
    onPatch({ layer_a_data: { ...layerA, quote_logic } });
  };
  const setRebateTreatment = (rebate_treatment) => {
    onPatch({
      layer_a_data: {
        ...layerA,
        rebate_treatment,
        rebate_owner: REBATE_OWNER_BY_TREATMENT[rebate_treatment] || 'none',
        factory_handles_rebate: rebate_treatment === 'factory_node'
      }
    });
  };
  const factoryQuote = totals?.factory_quote_rmb ?? (qty > 0 ? Number(totals?.cost_subtotal_rmb || 0) / qty : null);
  const tradeQuote = qty > 0 ? Number(totals?.sell_after_discount_rmb || 0) / qty : null;
  const channelQuote = qty > 0 ? Number(totals?.total_revenue_rmb || 0) / qty : null;
  const factoryCostAfterUplift = Number(totals?.factory_cost_after_uplift_rmb);
  const factoryMarginPct = Number.isFinite(Number(factoryQuote)) && Number(factoryQuote) > 0 && Number.isFinite(factoryCostAfterUplift)
    ? ((Number(factoryQuote) - factoryCostAfterUplift) / Number(factoryQuote)) * 100
    : null;
  const tradeRevenueRmb = Number(totals?.sell_after_discount_rmb || 0);
  const tradeMarginPct = Number.isFinite(Number(totals?.trade_stage_margin_pct))
    ? Number(totals.trade_stage_margin_pct)
    : (tradeRevenueRmb > 0
        ? ((tradeRevenueRmb
          - Number(totals?.cost_subtotal_rmb || 0)
          - Number(totals?.line_extra_cost_rmb || 0)
          - Number(totals?.shared_cost_rmb || 0)
          - Number(totals?.trade_cost_rmb || 0)
          - Math.max(0, -Number(totals?.export_rebate_rmb || 0))) / tradeRevenueRmb) * 100
        : null);
  const layerC = { ...DEFAULT_LAYER_C_DATA, ...(form.layer_c_data || {}) };
  const channelBasisMode = layerC.cost_basis_mode
    || (layerC.channel_cost_basis_mode === 'manual' ? 'manual' : 'inherit_trade');
  const manualChannelBasisPerUnit = toNumber(
    layerC.manual_basis_cost_per_unit_rmb ?? layerC.manual_channel_base_cost_rmb
  );
  const fallbackChannelCostBasisRmb = channelBasisMode === 'manual'
    ? manualChannelBasisPerUnit * Math.max(1, qty || 1)
    : Number(totals?.cost_subtotal_rmb || 0)
      + Number(totals?.line_extra_cost_rmb || 0)
      + Number(totals?.shared_cost_rmb || 0)
      + Number(totals?.trade_cost_rmb || 0)
      + Math.max(0, -Number(totals?.export_rebate_rmb || 0));
  const channelCostBasisRmb = Number.isFinite(Number(totals?.channel_cost_basis_rmb))
    ? Number(totals.channel_cost_basis_rmb)
    : fallbackChannelCostBasisRmb;
  const channelRevenueRmb = Number(totals?.sell_after_discount_rmb || 0);
  const channelMarginPct = Number.isFinite(Number(totals?.layer_c_data?.summary?.channel_margin_pct))
    ? Number(totals.layer_c_data.summary.channel_margin_pct)
    : (channelRevenueRmb > 0
        ? ((channelRevenueRmb - channelCostBasisRmb - Number(totals?.channel_cost_rmb || 0)) / channelRevenueRmb) * 100
        : null);
  const groupNetMarginPct = totals?.layer_e_data?.summary?.group_net_margin_pct ?? totals?.margin_pct;
  const entitySplit = totals?.layer_e_data?.split;
  const groupNetProfit = totals?.group_net_profit_rmb ?? totals?.gross_profit_rmb;
  const salesTotal = totals?.total_revenue_rmb ?? totals?.sell_after_discount_rmb ?? 0;
  const capitalDays = toNumber(layerA.capital_days || 0);
  const capitalOccupancy = capitalDays > 0 ? Number(totals?.cost_subtotal_rmb || 0) * capitalDays / 365 : 0;
  const smallBatchUpliftPct = toNumber(layerA.small_batch_uplift_pct || 0);
  const fxRateNumber = Number(form.fx_rate_to_rmb);
  const fxDisplay = form.currency === 'CNY'
    ? 'CNY 1.0000'
    : (Number.isFinite(fxRateNumber) && fxRateNumber > 0 && form.fx_rate_confirmed
        ? `${form.currency} ${fxRateNumber.toFixed(4)}`
        : `${form.currency} 未确认`);
  const domesticNetProfit = Number(entitySplit?.domestic_gross_profit_rmb || 0)
    - Number(entitySplit?.china_income_tax_rmb || 0);
  const overseasNetProfit = Number(entitySplit?.overseas_gross_profit_rmb || 0)
    - Number(entitySplit?.overseas_flow_tax_rmb || 0)
    - Number(entitySplit?.overseas_income_tax_rmb || 0);
  const validationRows = [
    validationHint || null,
    ...(Array.isArray(previewErrors) ? previewErrors.slice(0, 3).map((err) => `${err.field}: ${err.message}`) : [])
  ].filter(Boolean);

  if (collapsed) {
    return (
      <aside className="sticky top-4 ml-auto flex min-h-[16rem] w-14 flex-col items-center gap-2 self-start rounded-xl border border-[#e7dfcf] bg-[#fffdf8] p-2 shadow-sm">
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#d8ccb8] bg-[#fffaf0] text-lg font-semibold text-slate-700 hover:bg-[#f5efe3]"
          onClick={onToggleCollapsed}
          aria-label="展开驾驶舱"
          title="展开驾驶舱"
        >
          ‹
        </button>
        <div className="mt-1 flex flex-1 items-center justify-center">
          <div className="[writing-mode:vertical-rl] text-xs font-semibold tracking-normal text-slate-600">
            报价驾驶舱
          </div>
        </div>
        <div className="mb-1 text-center text-[11px] font-semibold leading-tight tabular-nums text-slate-800">
          {(groupNetMarginPct ?? 0).toFixed(1)}%
        </div>
      </aside>
    );
  }

  return (
    <aside className="sticky top-4 ml-auto max-h-[calc(100vh-2rem)] min-w-0 space-y-2 self-start overflow-y-auto rounded-xl border border-[#e7dfcf] bg-[#fffdf8] p-3 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">报价驾驶舱</div>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#d8ccb8] bg-[#fffaf0] text-base font-semibold text-slate-700 hover:bg-[#f5efe3]"
          onClick={onToggleCollapsed}
          aria-label="收起驾驶舱"
          title="收起驾驶舱"
        >
          ›
        </button>
      </div>
      <div className="rounded-lg border border-[#e7dfcf] bg-white p-3">
        <div className="grid grid-cols-[minmax(0,1.25fr)_minmax(6rem,0.75fr)] gap-3">
          <div className="min-w-0">
            <div className="text-[11px] text-slate-500">整单净利金额</div>
            <div className="break-words text-[clamp(1rem,1.45vw,1.25rem)] font-bold leading-tight tabular-nums text-slate-950">
              {formatRmb(groupNetProfit)}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-slate-500">利润率</div>
            <div className="break-words text-[clamp(1rem,1.45vw,1.25rem)] font-bold leading-tight tabular-nums text-slate-950">
              {(groupNetMarginPct ?? 0).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <DecisionMetric label="目标市场 / 汇率" value={`${layerA.target_market || '未填写'} · ${fxDisplay}`} />
	        <DecisionMetric
	          label="总退税额"
	          value={formatRmb(totals?.export_rebate_rmb)}
	          note={layerA.rebate_treatment === 'none'
	            ? '不参与利润'
	            : (Number(totals?.export_rebate_rmb || 0) < 0 ? '负数表示额外支出' : '')}
	        />
	      </div>

	      <div className="rounded-lg border border-[#e7dfcf] bg-[#fffdf8] p-2">
	        <label className="mb-2 block text-[11px] font-medium text-slate-500">退税归属</label>
	        <select
	          className="field text-sm"
	          value={layerA.rebate_treatment}
	          onChange={(e) => setRebateTreatment(e.target.value)}
	        >
	          {REBATE_TREATMENT_OPTIONS.map(([value, label]) => (
	            <option key={value} value={value}>{label}</option>
	          ))}
	        </select>
	        <p className="mt-1 text-[11px] leading-snug text-slate-500">
	          {REBATE_TREATMENT_OPTIONS.find(([value]) => value === layerA.rebate_treatment)?.[2]}
	        </p>
	      </div>

	      <div className="rounded-lg border border-[#e7dfcf] bg-[#fffdf8] p-2">
        <div className="mb-2 text-[11px] font-medium text-slate-500">报价逻辑</div>
        <div className="grid grid-cols-3 gap-1 rounded-lg border border-[#e7dfcf] bg-[#fffaf0] p-1">
          {QUOTE_LOGIC_OPTIONS.map(([value, label]) => {
            const active = layerA.quote_logic === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                className={[
                  'rounded-md border px-2 py-1.5 text-xs font-semibold transition',
                  active
                    ? 'border-[#a97416] bg-[#f8d48a] text-slate-950 shadow-sm ring-2 ring-[#d69a2d]'
                    : 'border-transparent bg-[#fffdf8] text-slate-600 hover:bg-[#f5efe3]'
                ].join(' ')}
                onClick={() => setQuoteLogic(value)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-[#e7dfcf] bg-[#fffdf8] p-2">
        <div className="mb-2 text-[11px] font-medium text-slate-500">订单数量 / 销售 / 资金占用</div>
        <div className="grid grid-cols-3 gap-2">
          <CockpitNumber label="报价数量" value={formatCompactNumber(qty, ' 件')} />
          <CockpitNumber label="销售总额" value={formatRmb(salesTotal)} />
          <CockpitNumber label="资金占用" value={formatRmb(capitalOccupancy)} note={capitalDays > 0 ? `${formatCompactNumber(capitalDays, ' 天')}` : '未设置天数'} />
        </div>
        <div className="mt-2 rounded-lg border border-[#e7dfcf] bg-white px-3 py-2 text-xs text-slate-600">
          小批量上浮：<strong className="text-slate-900">{smallBatchUpliftPct.toFixed(2)}%</strong>
        </div>
      </div>

      <DecisionStageRow
        label="工厂"
        items={[
          { label: '工厂报价', value: factoryQuote == null ? '—' : `${formatRmb(factoryQuote)}/件` },
          { label: '工厂环节利润率', value: factoryMarginPct == null ? '—' : `${factoryMarginPct.toFixed(1)}%` }
        ]}
      />
      <DecisionStageRow
        label="外贸"
        items={[
          { label: '外贸报价', value: tradeQuote == null ? '—' : `${formatRmb(tradeQuote)}/件` },
          { label: '外贸环节利润率', value: tradeMarginPct == null ? '—' : `${tradeMarginPct.toFixed(1)}%` }
        ]}
      />
      <DecisionStageRow
        label="电商"
        items={[
          { label: '平台售价', value: channelQuote == null ? '—' : `${formatRmb(channelQuote)}/件` },
          { label: '电商侧利润率', value: channelMarginPct == null ? '—' : `${Number(channelMarginPct).toFixed(1)}%` }
        ]}
      />
      <DecisionStageRow
        label="主体"
        items={[
          { label: '海外主体利润合计', value: formatRmb(overseasNetProfit) },
          { label: '国内主体利润合计', value: formatRmb(domesticNetProfit) },
          { label: '集团净利', value: formatRmb(groupNetProfit) }
        ]}
      />

      <div className="rounded-lg border border-[#e7dfcf] bg-white p-3">
        <div className="mb-2 text-[11px] font-medium text-slate-500">校验提示</div>
        {validationRows.length > 0 ? (
          <ul className="space-y-1 text-xs leading-snug text-amber-700">
            {validationRows.map((row, idx) => <li key={`${row}-${idx}`}>· {row}</li>)}
          </ul>
        ) : (
          <div className="text-xs font-medium text-emerald-700">主单和报价行已满足保存条件。</div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 pt-1">
        <button type="button" className="btn-ghost text-xs" disabled={saving || !canSave} onClick={onSaveDraft}>
          {saving ? '保存中…' : '保存草稿'}
        </button>
        <button type="button" className="btn-ghost text-xs" disabled={saving} onClick={onPreview}>
          预览报价单
        </button>
        <button type="button" className="rounded-md border border-[#c9b58c] bg-[#f6ead4] px-3 py-2 text-xs font-semibold text-slate-950 hover:bg-[#efe0c5] disabled:opacity-50" disabled={saving || !canSubmit} onClick={onSubmit}>
          {saving ? '提交中…' : '提交审批'}
        </button>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Wizard root
// ---------------------------------------------------------------------------

export default function QuoteWizard() {
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const { user, workspace } = useAuth();
  const hasFactoryLayer = hasWorkspaceLayer(workspace, 'A');
  const hasTradeLayer = hasWorkspaceLayer(workspace, 'B');
  const hasChannelLayer = hasWorkspaceLayer(workspace, 'C');
  const hasEntityLayer = hasWorkspaceLayer(workspace, 'E');

  // Only boss/finance can enter the wizard (sales must not enter cost prices).
  if (user?.role && !['boss', 'finance'].includes(user.role)) {
    return <Navigate to="/403" replace />;
  }

  // D5.2 — Cmd/Ctrl+Z undo. `formCtl.state` replaces the bare `form` state,
  // and `formCtl.setState` replaces `setForm`. The controller coalesces
  // rapid consecutive edits (typing) into a single history step, so undo
  // walks back at natural "pause" boundaries instead of per-keystroke.
  const formCtl = useUndoRedo(defaultForm());
  const form = formCtl.state;
  const setForm = formCtl.setState;
  useUndoRedoShortcuts(formCtl);

  const [quoteId, setQuoteId] = useState(urlId ? Number(urlId) : null);
  const [step, setStep] = useState(() => (hasFactoryLayer ? 'factory' : 'lines'));
  const [saving, setSaving] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [loaded, setLoaded] = useState(!urlId);  // if creating new, consider "loaded"
  const [leftRailCollapsed, setLeftRailCollapsed] = useState(false);
  const [customerPanelCollapsed, setCustomerPanelCollapsed] = useState(true);
  const [capabilitiesPanelCollapsed, setCapabilitiesPanelCollapsed] = useState(true);
  const [routeOptionsPanelCollapsed, setRouteOptionsPanelCollapsed] = useState(false);
  const [decisionPanelCollapsed, setDecisionPanelCollapsed] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const activeBusinessChain = form.business_chain || form.layer_a_data?.business_chain || 'factory_trade_channel_entity';
  const steps = useMemo(
    () => buildSteps(hasFactoryLayer, hasTradeLayer, hasChannelLayer, hasEntityLayer, activeBusinessChain),
    [hasFactoryLayer, hasTradeLayer, hasChannelLayer, hasEntityLayer, activeBusinessChain]
  );

  useEffect(() => {
    if (!steps.some((s) => s.key === step)) {
      setStep(steps[0]?.key || 'factory');
    }
  }, [step, steps]);

  // Load existing draft when /app/quotes/:id/edit is visited.
  useEffect(() => {
    if (!urlId) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await api.get(`/api/quotes/detail/${urlId}`);
        if (cancelled) return;
        if (detail.lifecycle_status && detail.lifecycle_status !== 'draft') {
          setSubmitErr(`报价状态为 ${detail.lifecycle_status}，不可编辑。`);
        }
        // D5.2 — reset() instead of setState() on initial draft load, so the
        // undo stack doesn't contain the placeholder defaultForm() baseline.
        formCtl.reset({
          customer_id: detail.customer_id ?? null,
          currency: detail.currency ?? 'CNY',
          fx_rate_to_rmb: detail.fx_rate_to_rmb ?? 1,
          fx_rate_confirmed: detail.currency === 'CNY' || Number(detail.fx_rate_to_rmb) > 0,
          business_chain: detail.business_chain || detail.layer_a_data?.business_chain || 'factory_trade_channel_entity',
          hs_code: detail.hs_code ?? '',
          tax_rebate_rate: detail.tax_rebate_rate ?? null,
          tax_rebate_source: detail.tax_rebate_source ?? null,
          discount_pct: detail.discount_pct ?? 0,
          tax_rate_pct: detail.tax_rate_pct ?? 0,
          lang: detail.lang ?? 'zh',
          line_items: Array.isArray(detail.lines) && detail.lines.length > 0
            ? detail.lines.map((l) => ({
	                sku: l.sku || '',
	                description: l.description || '',
	                hs_code: l.hs_code || (detail.lines.length === 1 ? detail.hs_code : ''),
	                tax_rebate_rate: l.tax_rebate_rate ?? (detail.lines.length === 1 ? detail.tax_rebate_rate : null),
	                tax_rebate_source: l.tax_rebate_source ?? (detail.lines.length === 1 ? detail.tax_rebate_source : null),
	                qty: l.qty || 1,
                unit_sell_rmb: l.unit_sell_rmb ?? 0,
                unit_cost_rmb: l.unit_cost_rmb ?? 0,
                line_extra_cost_rmb: l.line_extra_cost_rmb ?? 0
              }))
            : [emptyLine()],
          shared_costs: Array.isArray(detail.shared_costs)
            ? detail.shared_costs.map((s) => ({ label: s.label, amount_rmb: s.amount_rmb }))
            : [],
          layer_a_data: cloneLayerAData(detail.layer_a_data),
          layer_b_data: cloneLayerBData(detail.layer_b_data),
          layer_c_data: layerInputs(detail.layer_c_data, DEFAULT_LAYER_C_DATA),
          layer_e_data: layerInputs(detail.layer_e_data, DEFAULT_LAYER_E_DATA)
        });
        setSelectedCustomer(detail.customer_id && (detail.customer_name || detail.customer?.company_name)
          ? {
              id: detail.customer_id,
              company_name: detail.customer_name || detail.customer?.company_name
            }
          : null);
        setQuoteId(Number(urlId));
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        setSubmitErr(err.message || '加载草稿失败');
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [urlId]);

  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const applyCustomerChange = (customer_id, customer = null) => {
    setSelectedCustomer(customer_id == null ? null : customer);
    patch({ customer_id });
  };
  const selectQuotePath = (business_chain) => {
    patch(buildBusinessChainPatch(form, business_chain));
  };

  const preview = useMemo(() => previewTotals(form), [form]);
  const fxRateNumber = Number(form.fx_rate_to_rmb);
  const needsFxConfirmation = form.currency !== 'CNY';
  const hasValidFxRate = !needsFxConfirmation || (Number.isFinite(fxRateNumber) && fxRateNumber > 0);
  const fxReady = !needsFxConfirmation || (hasValidFxRate && Boolean(form.fx_rate_confirmed));

  const formReadyForSave = (() => {
    if (form.customer_id == null) return false;
    if (!fxReady) return false;
    return form.line_items.every((li) =>
      li.sku && li.sku.trim().length > 0 && li.qty > 0 && li.unit_sell_rmb >= 0 && li.unit_cost_rmb >= 0
    );
  })();

  const quoteReadinessHint = (() => {
    if (form.customer_id == null) return '报价主单缺少客户。你仍可先进入任意报价节点，保存/提交前需要补齐。';
    if (!hasValidFxRate) return `报价主单缺少有效汇率：1 ${form.currency} 兑换多少人民币。你仍可先进入任意报价节点，系统不会用 1.0000 默认放行，保存/提交前需要补齐。`;
    if (!fxReady) return '报价主单的汇率尚未确认。你仍可先进入任意报价节点，保存/提交前需要勾选确认。';
    const linesOk = form.line_items.every((li) =>
      li.sku && li.sku.trim().length > 0 && li.qty > 0 && li.unit_sell_rmb >= 0 && li.unit_cost_rmb >= 0
    );
    if (!linesOk) {
      return '报价行还不完整。四个节点可独立进入，保存/提交前需要补齐 SKU、数量、售价和成本。';
    }
    return null;
  })();

  async function doSave(nextStatus /* 'draft' | 'pending' */) {
    setSubmitErr(null);
    if (!formReadyForSave) {
      setSubmitErr(quoteReadinessHint || '请先补齐客户、报价行和已确认汇率。');
      return;
    }
    if (nextStatus === 'pending' && !preview.ok) {
      setSubmitErr('报价试算仍有错误，请先处理汇总区提示后再提交审批。');
      return;
    }
	    setSaving(true);
	    try {
	      const lineHsItems = deriveLineHsRebateItems(form.line_items);
	      const firstLineHs = lineHsItems[0] || null;
	      const payload = {
        customer_id: form.customer_id,
        currency: form.currency,
        fx_rate_to_rmb: form.fx_rate_to_rmb,
        lang: form.lang || 'zh',
        discount_pct: form.discount_pct,
        tax_rate_pct: form.tax_rate_pct,
        line_items: form.line_items,
        shared_costs: form.shared_costs
      };
      if (hasFactoryLayer) {
        payload.layer_a_data = form.layer_a_data || cloneLayerAData();
      }
	      if (hasTradeLayer) {
	        payload.hs_code = form.hs_code || firstLineHs?.hs_code || null;
	        payload.tax_rebate_rate = form.tax_rebate_rate ?? firstLineHs?.tax_rebate_rate ?? null;
	        payload.tax_rebate_source = form.tax_rebate_source ?? (firstLineHs ? 'line_item' : null);
	        payload.layer_b_data = cloneLayerBData({
	          ...form.layer_b_data,
	          hs_rebate_items: lineHsItems
	        });
	      }
      if (hasChannelLayer) {
        payload.layer_c_data = form.layer_c_data || { ...DEFAULT_LAYER_C_DATA };
      }
      if (hasEntityLayer) {
        payload.layer_e_data = layerInputs(form.layer_e_data, DEFAULT_LAYER_E_DATA);
      }
      let saved;
      const wasNew = !quoteId;
      if (quoteId) {
        saved = await api.patch(`/api/quotes/${quoteId}`, payload);
      } else {
        saved = await api.post('/api/quotes', payload);
        setQuoteId(saved.id);
      }

      // D5.4 — emit quote.created only on genuine first creation.
      if (wasNew) {
        try { trackEvent('quote.created', { quote_id: saved?.id, currency: saved?.currency }); } catch {}
      }

      if (nextStatus === 'pending') {
        saved = await api.post(`/api/quotes/${saved.id}/submit`, {});
        try { trackEvent('quote.submitted', { quote_id: saved?.id }); } catch {}
      }

      navigate(`/app/quotes/${saved.id}`);
    } catch (err) {
      setSubmitErr(err instanceof HttpError
        ? `${err.code}: ${err.message}`
        : (err.message || '保存失败'));
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) {
    return <div className="px-6 py-10 text-center text-slate-400">加载中…</div>;
  }

  const workspaceGridClass = [
    'grid gap-4',
    leftRailCollapsed && decisionPanelCollapsed
      ? 'xl:grid-cols-[3.5rem_minmax(0,1fr)_3.5rem]'
      : leftRailCollapsed
        ? 'xl:grid-cols-[3.5rem_minmax(0,1fr)_360px] 2xl:grid-cols-[3.5rem_minmax(0,1fr)_390px]'
        : decisionPanelCollapsed
          ? 'xl:grid-cols-[260px_minmax(0,1fr)_3.5rem] 2xl:grid-cols-[280px_minmax(0,1fr)_3.5rem]'
          : 'xl:grid-cols-[260px_minmax(0,1fr)_360px] 2xl:grid-cols-[280px_minmax(0,1fr)_390px]'
  ].join(' ');

  return (
    <div className="mx-auto flex h-[calc(100vh-3.5rem)] max-w-[1920px] flex-col px-4 py-4 sm:px-6">
      <header className="mb-3 flex shrink-0 items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">
            {quoteId ? `编辑报价链 #${quoteId}` : '新建报价链'}
          </h1>
          <p className="text-sm text-slate-500">
            {quoteId
              ? '报价主单与 A/B/C/E 节点可独立进入编辑，保存会整单替换当前草稿'
              : '报价主单 + 工厂报价、外贸报价、电商报价、主体利润四个节点可独立进入，保存/提交前再补齐必填资料'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* D5.2 undo/redo — keyboard shortcuts Cmd/Ctrl+Z / +Shift+Z are
              active globally via useUndoRedoShortcuts. The buttons are for
              discoverability and mouse-driven users. */}
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={formCtl.undo}
            disabled={!formCtl.canUndo}
            title="撤销 (Ctrl/Cmd+Z)"
            aria-label="撤销"
          >
            ↶ 撤销
            {formCtl.historySize > 0 && (
              <span className="ml-1 text-[10px] text-slate-400">
                ({formCtl.historySize})
              </span>
            )}
          </button>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={formCtl.redo}
            disabled={!formCtl.canRedo}
            title="重做 (Ctrl/Cmd+Shift+Z)"
            aria-label="重做"
          >
            ↷ 重做
            {formCtl.redoSize > 0 && (
              <span className="ml-1 text-[10px] text-slate-400">
                ({formCtl.redoSize})
              </span>
            )}
          </button>
          <Link to="/app/quotes" className="btn-ghost">← 返回列表</Link>
        </div>
      </header>

      {submitErr && <div className="alert-error mb-4">{submitErr}</div>}
      {quoteReadinessHint && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {quoteReadinessHint}
        </div>
      )}

      <div className={`${workspaceGridClass} min-h-0 flex-1`}>
        <QuoteLeftRail
          collapsed={leftRailCollapsed}
          onToggleCollapsed={() => setLeftRailCollapsed((value) => !value)}
          customerCollapsed={customerPanelCollapsed}
          onToggleCustomer={() => setCustomerPanelCollapsed((value) => !value)}
          capabilitiesCollapsed={capabilitiesPanelCollapsed}
          onToggleCapabilities={() => setCapabilitiesPanelCollapsed((value) => !value)}
          routeOptionsCollapsed={routeOptionsPanelCollapsed}
          onToggleRouteOptions={() => setRouteOptionsPanelCollapsed((value) => !value)}
          form={form}
          workspace={workspace}
          hasFactoryLayer={hasFactoryLayer}
          hasTradeLayer={hasTradeLayer}
          hasChannelLayer={hasChannelLayer}
          hasEntityLayer={hasEntityLayer}
          selectedCustomer={selectedCustomer}
          onCustomerChange={applyCustomerChange}
          onCustomerSelect={setSelectedCustomer}
          onSelectPath={selectQuotePath}
        />

        <div className="min-w-0 space-y-4 overflow-y-auto pr-1">
          <section className="rounded-xl border border-[#e7dfcf] bg-[#fffdf8] p-4 shadow-sm">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
              <div>
                <h2 className="text-base font-semibold text-slate-900">报价主单头部</h2>
                <p className="mt-1 text-sm text-slate-500">客户、市场、币种、数量、业务链路和场景摘要作为所有节点的共同上下文。</p>
              </div>
              <span className="rounded-full border border-[#e7dfcf] bg-[#fffaf0] px-3 py-1 text-xs font-medium text-slate-600">
                主单不锁节点，保存/提交前统一校验
              </span>
            </div>
            <StepBasics
              form={form}
              onPatch={patch}
              selectedCustomer={selectedCustomer}
              onCustomerSelect={setSelectedCustomer}
            />
          </section>

          <StepHeader
            active={step}
            steps={steps}
            onSelect={setStep}
            form={form}
            totals={preview.totals}
            formReadyForSave={formReadyForSave}
            previewOk={preview.ok}
          />

          {step === 'factory' && (
            <NodeWorkspaceShell
              stepKey="factory"
              form={form}
              totals={preview.totals}
              formReadyForSave={formReadyForSave}
              previewOk={preview.ok}
              onPatch={patch}
            >
              <StepFactoryQuote
                form={form}
                onPatch={patch}
                previewLines={preview.totals.lines}
                totals={preview.totals}
              />
            </NodeWorkspaceShell>
          )}
          {step === 'lines'  && (
            <NodeWorkspaceShell
              stepKey="lines"
              form={form}
              totals={preview.totals}
              formReadyForSave={formReadyForSave}
              previewOk={preview.ok}
              onPatch={patch}
            >
              <StepLines form={form} onPatch={patch} previewLines={preview.totals.lines} />
            </NodeWorkspaceShell>
          )}
          {step === 'trade' && (
            <NodeWorkspaceShell
              stepKey="trade"
              form={form}
              totals={preview.totals}
              formReadyForSave={formReadyForSave}
              previewOk={preview.ok}
              onPatch={patch}
            >
              <StepTradeCosts
                form={form}
                onPatch={patch}
                totals={preview.totals}
              />
            </NodeWorkspaceShell>
          )}
          {step === 'channel' && (
            <NodeWorkspaceShell
              stepKey="channel"
              form={form}
              totals={preview.totals}
              formReadyForSave={formReadyForSave}
              previewOk={preview.ok}
              onPatch={patch}
            >
              <StepChannelCosts form={form} onPatch={patch} totals={preview.totals} />
            </NodeWorkspaceShell>
          )}
          {step === 'entity' && (
            <NodeWorkspaceShell
              stepKey="entity"
              form={form}
              totals={preview.totals}
              formReadyForSave={formReadyForSave}
              previewOk={preview.ok}
              onPatch={patch}
            >
              <StepEntityCosts form={form} onPatch={patch} totals={preview.totals} />
            </NodeWorkspaceShell>
          )}
        </div>
        <QuoteDecisionPanel
          form={form}
          totals={preview.totals}
          onPatch={patch}
          saving={saving}
          canSave={formReadyForSave}
          canSubmit={preview.ok && formReadyForSave}
          validationHint={quoteReadinessHint}
          previewErrors={preview.errors}
          onSaveDraft={() => doSave('draft')}
          onPreview={() => {
            if (quoteId) {
              navigate(`/app/quotes/${quoteId}`);
              return;
            }
            setSubmitErr('先保存草稿后再预览报价单。');
          }}
          onSubmit={() => doSave('pending')}
          collapsed={decisionPanelCollapsed}
          onToggleCollapsed={() => setDecisionPanelCollapsed((value) => !value)}
        />
      </div>
    </div>
  );
}
